/**
 * SlidePlayer — self-contained capsule player overlay.
 * Renders the full slide flow (video → exercises → completion) for a given capsule.
 * Used by VideoFeedPlayer when the user taps "Play" in the feed.
 */
import {Feather} from '@expo/vector-icons'
// expo-av is not installed; Video slides are skipped at runtime.
// These stubs satisfy TypeScript without importing the missing package.
type AVPlaybackStatus =
  | {isLoaded: false}
  | {
      isLoaded: true
      isPlaying: boolean
      isBuffering: boolean
      positionMillis: number
      durationMillis?: number
      didJustFinish?: boolean
    }
// Minimal ref interface so video playback calls compile without importing expo-av.
type VideoRef = {
  pauseAsync: () => Promise<void>
  playAsync: () => Promise<void>
}
const ResizeMode = {COVER: 'cover' as const}
// Placeholder component - video slides are skipped; this is never rendered.
const Video = View as unknown as React.ComponentType<{
  ref?: React.Ref<VideoRef>
  source: {uri: string}
  style: unknown
  resizeMode?: string
  shouldPlay?: boolean
  isLooping?: boolean
  onPlaybackStatusUpdate?: (st: AVPlaybackStatus) => void
  onReadyForDisplay?: () => void
  onError?: () => void
}>
import {Fragment, useCallback, useEffect, useRef, useState} from 'react'
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  Image,
  PanResponder,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import * as Haptics from 'expo-haptics'
import {LinearGradient} from 'expo-linear-gradient'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  musicChoiceFromRecord,
  resolveBackgroundMusic,
} from '#/lib/backgroundMusic'
import {MUSIC_LIBRARY} from '#/lib/backgroundMusicLibrary'
import {startFlashbookMusic, stopFlashbookMusic} from '#/lib/flashbookAudio'
import {getCapsuleMaxPoints} from '#/lib/scoring'
import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {
  getMasterVolume,
  getMuted,
  playComplete,
  playCorrect,
  playPartial,
  playSlideAdvance,
  playWrong,
  setMasterVolume,
  setMuted,
  setSoundTheme,
  startBgMusic,
  stopBgMusic,
} from '#/lib/sounds'
import {offersReadAloud} from '#/lib/spokenKinds'
import {useCreatePlayMutation} from '#/state/queries/create-play'
import {useProfileQuery} from '#/state/queries/profile'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {CompletionScreen} from '#/components/capsules/CompletionScreen'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {TypeBadge} from '#/components/capsules/play/TypeBadge'
import {
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {FlashBookReadAloud} from '#/components/flashbooks/FlashBookReadAloud'
import {FlashBookRichBlock} from '#/components/flashbooks/FlashBookRichBlock'
import {
  type CapsuleModel,
  type Slide,
  SLIDE_TYPE_META,
  type SlideResult,
} from '#/types/capsule'

type AnswerResult = Omit<SlideResult, 'slideId' | 'type'>
type FBState = 'correct' | 'partial' | 'wrong' | 'solution'
type FeatherName = React.ComponentProps<typeof Feather>['name']

type VideoCallbacks = {
  onPlaybackStatusUpdate: (st: AVPlaybackStatus) => void
  onReadyForDisplay: () => void
  onError: () => void
}

const GRADIENTS: [string, string, string][] = [
  ['#0a0e1a', '#0d1e3a', '#0c1830'],
  ['#0e0a1a', '#1a0d2e', '#160a28'],
  ['#0a1a0e', '#0d2a1a', '#081a0e'],
  ['#1a0e0a', '#2a1a0d', '#1c0e08'],
]

function getGradient(id: string): [string, string, string] {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0
  return GRADIENTS[Math.abs(h) % GRADIENTS.length]
}

// ─── Shared ───────────────────────────────────────────────────────────────────

function FeedbackBar({
  state,
  points,
  maxPoints,
  explanation,
  solutionText,
  onNext,
  onRetry,
  onShowSolution,
  primaryColor,
}: {
  state: FBState
  points: number
  maxPoints: number
  explanation?: string
  solutionText?: string
  onNext: () => void
  onRetry?: () => void
  onShowSolution?: () => void
  primaryColor: string
}) {
  const {t: l} = useLingui()
  const accent =
    state === 'correct'
      ? '#22c55e'
      : state === 'partial'
        ? '#f59e0b'
        : state === 'solution'
          ? '#f59e0b'
          : '#ef4444'
  const icon =
    state === 'correct'
      ? 'check-circle'
      : state === 'partial'
        ? 'alert-circle'
        : state === 'solution'
          ? 'eye'
          : 'x-circle'
  const msg =
    state === 'correct'
      ? l`Correct! Well done!`
      : state === 'partial'
        ? l`Partial credit — ${points} / ${maxPoints} pts`
        : state === 'solution'
          ? l`Solution shown`
          : onRetry
            ? l`Wrong answer — try again!`
            : l`Wrong answer`

  useEffect(() => {
    if (state === 'correct') {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(
        () => {},
      )
      playCorrect()
    } else if (state === 'wrong') {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error).catch(
        () => {},
      )
      playWrong()
    } else if (state === 'partial') {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(
        () => {},
      )
      playPartial()
    }
  }, [state])

  return (
    <View
      style={[
        ss.fbBar,
        {backgroundColor: accent + '18', borderColor: accent + '55'},
      ]}>
      <View style={ss.fbTop}>
        <View style={ss.fbMsg}>
          <Feather name={icon} size={16} color={accent} />
          <Text style={[ss.fbMsgTxt, {color: accent}]}>{msg}</Text>
        </View>
        <View
          style={[
            ss.fbScore,
            {backgroundColor: accent + '22', borderColor: accent + '55'},
          ]}>
          <Text style={[ss.fbScoreTxt, {color: accent}]}>
            {points} / {maxPoints}
          </Text>
        </View>
      </View>
      {state === 'solution' && solutionText ? (
        <Text style={ss.fbSol}>{solutionText}</Text>
      ) : explanation ? (
        <Text style={ss.fbExp}>{explanation}</Text>
      ) : null}
      <View style={ss.fbActions}>
        {(state === 'wrong' || state === 'partial') && onRetry && (
          <TouchableOpacity
            accessibilityRole="button"
            onPress={onRetry}
            style={[ss.ghostBtn, {borderColor: '#ef444466'}]}>
            <Feather name="refresh-cw" size={12} color="#ef4444" />
            <Text style={[ss.ghostTxt, {color: '#ef4444'}]}>
              <Trans>Try again</Trans>
            </Text>
          </TouchableOpacity>
        )}
        {(state === 'wrong' || state === 'partial') && onShowSolution && (
          <TouchableOpacity
            accessibilityRole="button"
            onPress={onShowSolution}
            style={[ss.ghostBtn, {borderColor: 'rgba(255,255,255,0.2)'}]}>
            <Feather name="eye" size={12} color="rgba(255,255,255,0.85)" />
            <Text style={[ss.ghostTxt, {color: 'rgba(255,255,255,0.85)'}]}>
              <Trans>Show solution</Trans>
            </Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onNext}
          style={[ss.nextBtn, {backgroundColor: primaryColor}]}>
          <Text style={ss.nextTxt}>
            <Trans>Continue</Trans>
          </Text>
          <Feather name="arrow-right" size={14} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  )
}

// ─── Slide components ─────────────────────────────────────────────────────────

// VideoSlide is retained for reference but not rendered — video slides are
// filtered out before SlidePlayer processes them.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function VideoSlide({
  slide,
  title,
  primaryColor,
  onNext,
  externalVideoRef,
  callbacksRef,
  isPreview,
}: {
  slide: Slide
  title: string
  primaryColor: string
  onNext: () => void
  externalVideoRef?: React.RefObject<VideoRef | null>
  callbacksRef?: React.MutableRefObject<VideoCallbacks | null>
  isPreview?: boolean
}) {
  const internalVideoRef = useRef<VideoRef>(null)
  const activeVideoRef = externalVideoRef ?? internalVideoRef

  const [status, setStatus] = useState<
    'loading' | 'playing' | 'paused' | 'ended' | 'error'
  >('loading')
  const [showControls, setShowControls] = useState(true)
  const controlsTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const loadTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const didAdvance = useRef(false)

  const scheduleAdvance = (delay: number) => {
    if (didAdvance.current) return
    didAdvance.current = true
    if (advanceTimer.current) clearTimeout(advanceTimer.current)
    advanceTimer.current = setTimeout(onNext, delay)
  }

  const hideControlsAfter = () => {
    if (controlsTimer.current) clearTimeout(controlsTimer.current)
    controlsTimer.current = setTimeout(() => setShowControls(false), 3000)
  }

  const handlePlaybackStatusUpdate = (st: AVPlaybackStatus) => {
    if (!st.isLoaded) return
    // After the isLoaded guard, TypeScript narrows st to AVPlaybackStatusSuccess.
    // Position-based end detection — expo-av didJustFinish is unreliable on web.
    const isAtEnd =
      !!st.durationMillis &&
      st.durationMillis > 0 &&
      st.positionMillis >= st.durationMillis - 300 &&
      !st.isPlaying &&
      !st.isBuffering
    if (st.didJustFinish || (isAtEnd && status !== 'ended')) {
      setStatus('ended')
      setShowControls(true)
      scheduleAdvance(1800)
    } else if (status === 'loading') {
      if (st.isPlaying) {
        setStatus('playing')
        hideControlsAfter()
      } else if (!st.isBuffering) {
        setStatus('paused')
      }
    }
  }

  const handleReadyForDisplay = () => {
    setStatus(s => (s === 'loading' ? 'paused' : s))
  }

  const handleError = () => {
    setStatus('error')
    scheduleAdvance(2000)
  }

  // Keep callbacksRef always up-to-date with current closures (no deps = every render)
  useEffect(() => {
    if (callbacksRef) {
      callbacksRef.current = {
        onPlaybackStatusUpdate: handlePlaybackStatusUpdate,
        onReadyForDisplay: handleReadyForDisplay,
        onError: handleError,
      }
    }
    return () => {
      if (callbacksRef) callbacksRef.current = null
    }
  })

  useEffect(() => {
    if (status === 'loading') {
      loadTimeout.current = setTimeout(() => {
        setStatus(s => (s === 'loading' ? 'error' : s))
        scheduleAdvance(2000)
      }, 12000)
    } else if (loadTimeout.current) {
      clearTimeout(loadTimeout.current)
      loadTimeout.current = undefined
    }
    return () => {
      if (loadTimeout.current) clearTimeout(loadTimeout.current)
    }
  }, [status])

  const togglePlay = async () => {
    if (!activeVideoRef.current) return
    setShowControls(true)
    if (status === 'playing') {
      await activeVideoRef.current.pauseAsync()
      setStatus('paused')
    } else if (status === 'ended') {
      // Tap after video ends → advance immediately (don't replay)
      if (advanceTimer.current) {
        clearTimeout(advanceTimer.current)
        advanceTimer.current = undefined
      }
      onNext()
    } else {
      await activeVideoRef.current.playAsync()
      setStatus('playing')
      hideControlsAfter()
    }
  }

  useEffect(
    () => () => {
      if (controlsTimer.current) clearTimeout(controlsTimer.current)
      if (advanceTimer.current) clearTimeout(advanceTimer.current)
    },
    [],
  )

  const hasVideo = !!slide.videoUrl

  if (!hasVideo) {
    return (
      <View style={[StyleSheet.absoluteFill, {padding: 20, gap: 14}]}>
        <View style={ss.videoBox}>
          <Feather name="video" size={52} color="rgba(255,255,255,0.18)" />
          <Text style={ss.videoBoxLabel}>No video uploaded</Text>
        </View>
        <Text style={ss.videoTitle}>{slide.contentTitle || title}</Text>
        {!!slide.contentText && (
          <Text style={ss.videoDesc}>{slide.contentText}</Text>
        )}
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onNext}
          style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
          <Text style={ss.primaryBtnTxt}>Start exercises</Text>
          <Feather name="arrow-right" size={16} color="#fff" />
        </TouchableOpacity>
      </View>
    )
  }

  // When externalVideoRef is provided the <Video> is rendered at the screen root
  // as a fullscreen background. This component only renders the overlay controls.
  return (
    <View style={externalVideoRef ? StyleSheet.absoluteFill : ss.videoSlide}>
      {/* Own <Video> only when NOT using the screen-level fullscreen video */}
      {!externalVideoRef && (
        <Pressable
          accessibilityRole="button"
          style={ss.videoWrap}
          onPress={togglePlay}>
          <Video
            ref={internalVideoRef}
            source={{uri: slide.videoUrl!}}
            style={ss.video}
            resizeMode={ResizeMode.COVER}
            shouldPlay
            isLooping={false}
            onPlaybackStatusUpdate={handlePlaybackStatusUpdate}
            onReadyForDisplay={handleReadyForDisplay}
            onError={handleError}
          />
        </Pressable>
      )}

      {/* Tap area for play/pause when using screen-level video */}
      {externalVideoRef && (
        <Pressable
          accessibilityRole="button"
          style={StyleSheet.absoluteFill}
          onPress={togglePlay}
        />
      )}

      {/* Loading */}
      {status === 'loading' && (
        <View style={ss.videoCenter} pointerEvents="none">
          <ActivityIndicator size="large" color="#fff" />
        </View>
      )}

      {/* Play/pause icon */}
      {showControls &&
        status !== 'loading' &&
        status !== 'ended' &&
        status !== 'error' && (
          <View style={ss.videoCenter} pointerEvents="none">
            <View style={ss.videoPlayBtn}>
              <Feather
                name={status === 'playing' ? 'pause' : 'play'}
                size={28}
                color="#fff"
                style={status === 'playing' ? undefined : {marginLeft: 3}}
              />
            </View>
          </View>
        )}

      {/* Ended overlay */}
      {status === 'ended' && (
        <View style={ss.videoCenter} pointerEvents="none">
          <View
            style={{
              backgroundColor: 'rgba(0,0,0,0.75)',
              borderRadius: 20,
              padding: 16,
              alignItems: 'center',
              gap: 8,
            }}>
            <Feather name="check-circle" size={28} color="#22c55e" />
            <Text style={{color: '#fff', fontSize: 14, fontWeight: '700'}}>
              Video finished
            </Text>
            <Text style={{color: 'rgba(255,255,255,0.5)', fontSize: 12}}>
              Tap to continue…
            </Text>
          </View>
        </View>
      )}

      {/* Error overlay */}
      {status === 'error' && (
        <View style={ss.videoCenter} pointerEvents="none">
          <View
            style={{
              backgroundColor: 'rgba(0,0,0,0.75)',
              borderRadius: 20,
              padding: 16,
              alignItems: 'center',
              gap: 8,
            }}>
            <Feather name="alert-triangle" size={28} color="#ef4444" />
            <Text style={{color: '#fff', fontSize: 14, fontWeight: '700'}}>
              Couldn't load video
            </Text>
            <Text style={{color: 'rgba(255,255,255,0.5)', fontSize: 12}}>
              Skipping…
            </Text>
          </View>
        </View>
      )}

      {/* Bottom gradient + title */}
      <LinearGradient
        colors={['transparent', 'rgba(0,0,0,0.6)', 'rgba(0,0,0,0.88)']}
        pointerEvents="none"
        style={ss.videoInfoOverlay}>
        <Text style={ss.videoTitle}>{slide.contentTitle || title}</Text>
        {!!slide.contentText && (
          <Text style={ss.videoDesc}>{slide.contentText}</Text>
        )}
      </LinearGradient>

      {/* Preview-mode skip button — teachers can skip the video */}
      {isPreview && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => {
            if (advanceTimer.current) {
              clearTimeout(advanceTimer.current)
              advanceTimer.current = undefined
            }
            didAdvance.current = true
            onNext()
          }}
          style={ss.videoSkipBtn}
          activeOpacity={0.75}>
          <Feather name="skip-forward" size={13} color="#fff" />
          <Text style={ss.videoSkipTxt}>Skip video</Text>
        </TouchableOpacity>
      )}
    </View>
  )
}

/**
 * A reading block, in the capsule player.
 *
 * It used to be a heading, a paragraph and Continue, whatever the block was — so
 * the capsule's own reading blocks arrived flattened: a quotation as ordinary
 * prose, a table as a sentence, a formula as its LaTeX source. A `content` block
 * that knows how it presents itself is now *printed* the way its theme prints it
 * (see `#/components/flashbooks/FlashBookRichBlock`), and a block the creator gave
 * a voice to — writing, with a clip they recorded or uploaded — is played through
 * the same transport a book uses. Nothing else is: a passage nobody narrated has
 * no voice at all.
 *
 * The capsule's own Continue stays: it is how the learner moves on here, and the
 * reading is something to *do* on this slide rather than a page to turn.
 */
function ContentSlide({
  slide,
  primaryColor,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onNext: () => void
}) {
  const design = useFlashBookPlayDesign()
  const palette = useFlashBookPlayPalette()

  const kind = slide.contentKind ?? 'text'
  const isRich = kind !== 'text'
  const title = slide.contentTitle ?? ''
  const body = slide.contentText ?? ''
  // The creator's own clip, and nothing else — see `offersReadAloud`.
  const spoken = offersReadAloud(slide)

  return (
    <View style={ss.content}>
      {isRich ? (
        <FlashBookRichBlock
          block={slide}
          design={design}
          accent={primaryColor}
        />
      ) : (
        <>
          {!!title && <Text style={ss.contentH2}>{title}</Text>}
          {!!body && <Text style={ss.contentBody}>{body}</Text>}
        </>
      )}

      {spoken && (
        <FlashBookReadAloud
          source={{url: slide.readingAudioUrl!}}
          accent={primaryColor}
          mutedColor={palette.muted}
          design={design}
        />
      )}

      <TouchableOpacity
        accessibilityRole="button"
        onPress={onNext}
        style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
        <Text style={ss.primaryBtnTxt}>Continue</Text>
        <Feather name="arrow-right" size={16} color="#fff" />
      </TouchableOpacity>
    </View>
  )
}

function SingleChoiceSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const {t: l} = useLingui()
  const opts = slide.options ?? []
  const [selected, setSelected] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const pick = (id: string, correct: boolean) => {
    if (done) return
    setSelected(id)
    setDone(true)
    onAnswer({
      isCorrect: correct,
      points: correct ? (slide.points ?? 1) : 0,
      timeSpentMs: 0,
      userAnswer: id,
    })
  }
  const correct = opts.find(o => o.isCorrect)
  const isRight = done && !!opts.find(o => o.id === selected && o.isCorrect)
  const col = (o: (typeof opts)[0]) => {
    if (!done)
      return {bg: 'rgba(255,255,255,0.07)', b: 'rgba(255,255,255,0.14)'}
    if (o.isCorrect) return {bg: '#22c55e22', b: '#22c55e'}
    if (o.id === selected) return {bg: '#ef444422', b: '#ef4444'}
    return {bg: 'rgba(255,255,255,0.03)', b: 'rgba(255,255,255,0.07)'}
  }
  return (
    <View style={ss.content}>
      <Text style={ss.question}>{slide.question}</Text>
      <View style={ss.optList}>
        {opts.map(o => {
          const {bg, b} = col(o)
          return (
            <Pressable
              accessibilityRole="button"
              key={o.id}
              onPress={() => pick(o.id, o.isCorrect)}
              style={[ss.optRow, {backgroundColor: bg, borderColor: b}]}>
              <View
                style={[
                  ss.optRadio,
                  {
                    borderColor:
                      done && o.isCorrect
                        ? '#22c55e'
                        : done && o.id === selected
                          ? '#ef4444'
                          : primaryColor + '80',
                  },
                ]}>
                {done && o.isCorrect && (
                  <View style={[ss.optDot, {backgroundColor: '#22c55e'}]} />
                )}
                {done && o.id === selected && !o.isCorrect && (
                  <Feather name="x" size={10} color="#ef4444" />
                )}
              </View>
              <Text
                style={[
                  ss.optLabel,
                  {
                    opacity:
                      done && !o.isCorrect && o.id !== selected ? 0.4 : 1,
                  },
                ]}>
                {o.label}
              </Text>
              {done && o.isCorrect && (
                <Feather name="check" size={14} color="#22c55e" />
              )}
            </Pressable>
          )
        })}
      </View>
      {done && (
        <FeedbackBar
          state={isRight ? 'correct' : 'wrong'}
          points={isRight ? (slide.points ?? 1) : 0}
          maxPoints={slide.points ?? 1}
          explanation={slide.explanation}
          solutionText={
            !isRight ? l`Correct answer: ${correct?.label}` : undefined
          }
          onNext={onNext}
          primaryColor={primaryColor}
        />
      )}
    </View>
  )
}

function MultipleChoiceSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const opts = slide.options ?? []
  const correctIds = new Set(opts.filter(o => o.isCorrect).map(o => o.id))
  const maxPts = slide.points ?? correctIds.size
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const toggle = (id: string) => {
    if (checked) return
    setSel(p => {
      const n = new Set(p)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }
  // Partial-credit scoring: +1 for each correct pick, -1 for each wrong pick.
  // The raw score is then scaled proportionally to maxPts.
  // A pure penalty formula (p-- per wrong) is intentionally kept mild: picking
  // 1 correct + 1 wrong on a 2-correct question yields raw = 0, not negative.
  // This prevents a student who guesses partially correctly from being penalised
  // more harshly than a student who guesses nothing at all (raw=0 in both cases
  // maps to 0 pts after max(0,...)).  If you want stricter penalties, replace
  // `p--` with `p -= 2` — but keep it consistent between files.
  const calcPts = (s: Set<string>) => {
    let p = 0
    s.forEach(id => (correctIds.has(id) ? p++ : p--))
    return Math.max(0, p)
  }
  const isExactMatch = (s: Set<string>) =>
    s.size === correctIds.size && Array.from(s).every(id => correctIds.has(id))
  const verify = () => {
    const raw = calcPts(sel)
    const allOk = isExactMatch(sel)
    const pts = allOk
      ? maxPts
      : Math.max(0, Math.round((raw / Math.max(correctIds.size, 1)) * maxPts))
    onAnswer({
      isCorrect: allOk,
      points: pts,
      timeSpentMs: 0,
      userAnswer: Array.from(sel),
    })
    setChecked(true)
  }
  const retry = () => {
    setSel(new Set())
    setChecked(false)
    setRetried(true)
  }
  const allOk = isExactMatch(sel)
  const pts = checked
    ? allOk
      ? maxPts
      : Math.max(
          0,
          Math.round((calcPts(sel) / Math.max(correctIds.size, 1)) * maxPts),
        )
    : 0
  const fbState: FBState = showSol
    ? 'solution'
    : allOk
      ? 'correct'
      : pts > 0
        ? 'partial'
        : 'wrong'
  const col = (o: (typeof opts)[0]) => {
    const isSel = sel.has(o.id)
    if (!checked && !showSol)
      return {
        bg: isSel ? primaryColor + '18' : 'rgba(255,255,255,0.07)',
        b: isSel ? primaryColor : 'rgba(255,255,255,0.14)',
      }
    if (showSol)
      return o.isCorrect
        ? {bg: '#f59e0b18', b: '#f59e0b'}
        : {bg: 'rgba(255,255,255,0.04)', b: 'rgba(255,255,255,0.08)'}
    if (o.isCorrect && isSel) return {bg: '#22c55e22', b: '#22c55e'}
    if (o.isCorrect) return {bg: '#22c55e11', b: '#22c55e55'}
    if (isSel) return {bg: '#ef444422', b: '#ef4444'}
    return {bg: 'rgba(255,255,255,0.04)', b: 'rgba(255,255,255,0.08)'}
  }
  return (
    <View style={ss.content}>
      <Text style={ss.question}>{slide.question}</Text>
      <View style={ss.optList}>
        {opts.map(o => {
          const {bg, b} = col(o)
          const isSel = sel.has(o.id)
          const icon =
            checked && o.isCorrect && isSel
              ? 'check-square'
              : checked && !o.isCorrect && isSel
                ? 'x-square'
                : isSel
                  ? 'check-square'
                  : 'square'
          const ic = checked
            ? o.isCorrect && isSel
              ? '#22c55e'
              : !o.isCorrect && isSel
                ? '#ef4444'
                : o.isCorrect
                  ? '#22c55e66'
                  : 'rgba(255,255,255,0.25)'
            : isSel
              ? primaryColor
              : 'rgba(255,255,255,0.25)'
          return (
            <Pressable
              accessibilityRole="button"
              key={o.id}
              onPress={() => toggle(o.id)}
              style={[ss.optRow, {backgroundColor: bg, borderColor: b}]}>
              <Feather name={icon} size={20} color={ic} />
              <Text
                style={[
                  ss.optLabel,
                  {
                    opacity:
                      checked && !showSol && !o.isCorrect && !isSel ? 0.4 : 1,
                  },
                ]}>
                {o.label}
              </Text>
              {showSol && o.isCorrect && (
                <Feather name="check" size={14} color="#f59e0b" />
              )}
            </Pressable>
          )
        })}
      </View>
      {!checked && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          disabled={sel.size === 0}
          style={[
            ss.verifyBtn,
            {
              backgroundColor:
                sel.size > 0 ? primaryColor : 'rgba(255,255,255,0.1)',
              opacity: sel.size > 0 ? 1 : 0.5,
            },
          ]}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={ss.verifyTxt}>Check</Text>
        </TouchableOpacity>
      )}
      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={opts
            .filter(o => o.isCorrect)
            .map(o => o.label)
            .join(', ')}
          onNext={onNext}
          onRetry={!retried ? retry : undefined}
          onShowSolution={
            !showSol && retried ? () => setShowSol(true) : undefined
          }
          primaryColor={primaryColor}
        />
      )}
    </View>
  )
}

function TrueFalseSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const {t: l} = useLingui()
  const [selected, setSelected] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const pick = (val: string) => {
    if (done) return
    setSelected(val)
    setDone(true)
    const ok = slide.answer === val
    onAnswer({
      isCorrect: ok,
      points: ok ? (slide.points ?? 1) : 0,
      timeSpentMs: 0,
      userAnswer: val,
    })
  }
  const isRight = done && slide.answer === selected
  return (
    <View style={ss.content}>
      <Text style={ss.question}>{slide.question}</Text>
      <View style={ss.tfRow}>
        {(['true', 'false'] as const).map(val => {
          const isSel = selected === val
          const correct = slide.answer === val
          const clr = val === 'true' ? '#22c55e' : '#ef4444'
          let bg = clr + '12'
          let b = clr + '44'
          if (done && isSel && correct) {
            bg = clr + '33'
            b = clr
          } else if (done && isSel) {
            bg = '#ef444433'
            b = '#ef4444'
          } else if (done && correct) {
            bg = clr + '22'
            b = clr + '88'
          }
          return (
            <Pressable
              accessibilityRole="button"
              key={val}
              onPress={() => pick(val)}
              style={[ss.tfBtn, {backgroundColor: bg, borderColor: b}]}>
              <Feather
                name={val === 'true' ? 'check' : 'x'}
                size={32}
                color={clr}
              />
              <Text style={[ss.tfLabel, {color: clr}]}>
                {val === 'true' ? l`True` : l`False`}
              </Text>
              {done && correct && (
                <Feather
                  name="check-circle"
                  size={16}
                  color={clr}
                  style={ss.tfCheck}
                />
              )}
            </Pressable>
          )
        })}
      </View>
      {done && (
        <FeedbackBar
          state={isRight ? 'correct' : 'wrong'}
          points={isRight ? (slide.points ?? 1) : 0}
          maxPoints={slide.points ?? 1}
          explanation={slide.explanation}
          solutionText={
            !isRight
              ? l`Correct answer: ${slide.answer === 'true' ? 'True ✓' : 'False ✗'}`
              : undefined
          }
          onNext={onNext}
          primaryColor={primaryColor}
        />
      )}
    </View>
  )
}

function MatchingSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const pairs = slide.pairs ?? []
  const [shuffled] = useState(() =>
    [...pairs.map(p => ({id: p.id, text: p.right}))].sort(
      () => Math.random() - 0.5,
    ),
  )
  const [selLeft, setSelLeft] = useState<string | null>(null)
  const [matched, setMatched] = useState<Record<string, string>>({})
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const maxPts = slide.points ?? pairs.length
  const allDone = Object.keys(matched).length >= pairs.length
  useEffect(() => {
    if (allDone && !checked) {
      setTimeout(() => {
        const okC = pairs.filter(p => matched[p.id] === p.id).length
        const pts = Math.round((okC / pairs.length) * maxPts)
        onAnswer({
          isCorrect: okC === pairs.length,
          points: pts,
          timeSpentMs: 0,
          userAnswer: matched,
        })
        setChecked(true)
      }, 300)
    }
  }, [allDone, checked, matched, pairs, maxPts, onAnswer])
  const handleLeft = (id: string) => {
    if (checked) return
    if (matched[id]) {
      setMatched(m => {
        const n = {...m}
        delete n[id]
        return n
      })
      return
    }
    setSelLeft(id === selLeft ? null : id)
  }
  const handleRight = (rId: string) => {
    if (checked || !selLeft) return
    if (Object.values(matched).includes(rId)) return
    Haptics.selectionAsync().catch(() => {})
    setMatched(m => ({...m, [selLeft]: rId}))
    setSelLeft(null)
  }
  const retry = () => {
    setMatched({})
    setSelLeft(null)
    setChecked(false)
    setShowSol(false)
    setRetried(true)
  }
  const okCount = pairs.filter(p => matched[p.id] === p.id).length
  const pts = pairs.length ? Math.round((okCount / pairs.length) * maxPts) : 0
  const fbState: FBState = showSol
    ? 'solution'
    : okCount === pairs.length
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'
  return (
    <View style={ss.content}>
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}
      <View style={ss.matchGrid}>
        <View style={ss.matchCol}>
          {pairs.map(p => {
            const isA = selLeft === p.id
            const isMat = !!matched[p.id]
            const ok = checked && matched[p.id] === p.id
            const bad = checked && isMat && !ok
            return (
              <TouchableOpacity
                accessibilityRole="button"
                key={p.id}
                onPress={() => handleLeft(p.id)}
                style={[
                  ss.matchItem,
                  {
                    borderColor: ok
                      ? '#22c55e'
                      : bad
                        ? '#ef4444'
                        : isA
                          ? primaryColor
                          : isMat
                            ? primaryColor + '77'
                            : 'rgba(255,255,255,0.18)',
                    backgroundColor: ok
                      ? '#22c55e11'
                      : bad
                        ? '#ef444411'
                        : isA
                          ? primaryColor + '18'
                          : 'rgba(255,255,255,0.05)',
                  },
                ]}>
                <Text style={ss.matchTxt}>{p.left}</Text>
                {isMat && !checked && (
                  <Feather name="link-2" size={11} color={primaryColor} />
                )}
                {ok && <Feather name="check" size={12} color="#22c55e" />}
                {bad && <Feather name="x" size={12} color="#ef4444" />}
              </TouchableOpacity>
            )
          })}
        </View>
        <View style={ss.matchArrow}>
          <Feather name="arrow-right" size={16} color="rgba(255,255,255,0.2)" />
        </View>
        <View style={ss.matchCol}>
          {shuffled.map(item => {
            const usedBy = Object.entries(matched).find(
              ([, v]) => v === item.id,
            )
            const isMat = !!usedBy
            const ok = checked && usedBy && usedBy[0] === item.id
            const bad = checked && isMat && !ok
            return (
              <TouchableOpacity
                accessibilityRole="button"
                key={item.id}
                onPress={() => handleRight(item.id)}
                style={[
                  ss.matchItem,
                  {
                    borderColor: ok
                      ? '#22c55e'
                      : bad
                        ? '#ef4444'
                        : isMat
                          ? primaryColor + '77'
                          : selLeft
                            ? primaryColor + '44'
                            : 'rgba(255,255,255,0.18)',
                    backgroundColor: ok
                      ? '#22c55e11'
                      : bad
                        ? '#ef444411'
                        : isMat
                          ? primaryColor + '11'
                          : 'rgba(255,255,255,0.05)',
                    opacity: isMat && !selLeft ? 0.7 : 1,
                  },
                ]}>
                <Text style={ss.matchTxt}>{item.text}</Text>
              </TouchableOpacity>
            )
          })}
        </View>
      </View>
      {!allDone && !checked && (
        <Text style={ss.matchHint}>
          Select a term on the left, then its match on the right
        </Text>
      )}
      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={pairs.map(p => `${p.left} → ${p.right}`).join(' · ')}
          onNext={onNext}
          primaryColor={primaryColor}
          onRetry={!retried && !showSol ? retry : undefined}
          onShowSolution={!showSol ? () => setShowSol(true) : undefined}
        />
      )}
    </View>
  )
}

function OrderingSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const items = slide.orderItems ?? []
  const [shuffled] = useState(() => [...items].sort(() => Math.random() - 0.5))
  const [order, setOrder] = useState(shuffled.map(i => i.id))
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const maxPts = slide.points ?? items.length
  const swap = (i: number, j: number) => {
    Haptics.selectionAsync().catch(() => {})
    setOrder(o => {
      const n = [...o]
      ;[n[i], n[j]] = [n[j], n[i]]
      return n
    })
  }
  const verify = () => {
    const okC = order.filter(
      (id, idx) => slide.correctOrder?.[idx] === id,
    ).length
    const pts = items.length ? Math.round((okC / items.length) * maxPts) : 0
    onAnswer({
      isCorrect: okC === items.length,
      points: pts,
      timeSpentMs: 0,
      userAnswer: order,
    })
    setChecked(true)
  }
  const retry = () => {
    setOrder(shuffled.map(i => i.id))
    setChecked(false)
    setShowSol(false)
    setRetried(true)
  }
  const displayOrder = showSol ? (slide.correctOrder ?? order) : order
  const okCount = checked
    ? order.filter((id, idx) => slide.correctOrder?.[idx] === id).length
    : 0
  const pts = items.length ? Math.round((okCount / items.length) * maxPts) : 0
  const fbState: FBState = showSol
    ? 'solution'
    : okCount === items.length
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'
  return (
    <View style={ss.content}>
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}
      <Text style={ss.orderHint}>Put these items in the correct order:</Text>
      <View style={ss.orderList}>
        {displayOrder.map((id, idx) => {
          const item = items.find(i => i.id === id)
          if (!item) return null
          const ok = checked && !showSol && slide.correctOrder?.[idx] === id
          const bad = checked && !showSol && !ok
          return (
            <View
              key={id}
              style={[
                ss.orderRow,
                {
                  borderColor: showSol
                    ? '#d97706'
                    : ok
                      ? '#22c55e'
                      : bad
                        ? '#ef4444'
                        : 'rgba(255,255,255,0.18)',
                  backgroundColor: showSol
                    ? '#d9770611'
                    : ok
                      ? '#22c55e11'
                      : bad
                        ? '#ef444411'
                        : 'rgba(255,255,255,0.05)',
                },
              ]}>
              <View
                style={[
                  ss.orderNum,
                  {
                    backgroundColor: showSol
                      ? '#d9770622'
                      : ok
                        ? '#22c55e22'
                        : bad
                          ? '#ef444422'
                          : primaryColor + '22',
                  },
                ]}>
                <Text
                  style={[
                    ss.orderNumTxt,
                    {
                      color: showSol
                        ? '#d97706'
                        : ok
                          ? '#22c55e'
                          : bad
                            ? '#ef4444'
                            : primaryColor,
                    },
                  ]}>
                  {idx + 1}
                </Text>
              </View>
              <Text style={ss.orderLabel}>{item.label}</Text>
              {!checked && (
                <View style={ss.orderArrows}>
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => idx > 0 && swap(idx, idx - 1)}>
                    <Feather
                      name="chevron-up"
                      size={22}
                      color={
                        idx > 0
                          ? 'rgba(255,255,255,0.6)'
                          : 'rgba(255,255,255,0.15)'
                      }
                    />
                  </Pressable>
                  <Pressable
                    accessibilityRole="button"
                    onPress={() =>
                      idx < order.length - 1 && swap(idx, idx + 1)
                    }>
                    <Feather
                      name="chevron-down"
                      size={22}
                      color={
                        idx < order.length - 1
                          ? 'rgba(255,255,255,0.6)'
                          : 'rgba(255,255,255,0.15)'
                      }
                    />
                  </Pressable>
                </View>
              )}
              {ok && <Feather name="check-circle" size={16} color="#22c55e" />}
              {bad && <Feather name="x-circle" size={16} color="#ef4444" />}
            </View>
          )
        })}
      </View>
      {!checked && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          style={[ss.verifyBtn, {backgroundColor: primaryColor}]}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={ss.verifyTxt}>Check order</Text>
        </TouchableOpacity>
      )}
      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          onNext={onNext}
          onRetry={!retried && !showSol ? retry : undefined}
          onShowSolution={!showSol ? () => setShowSol(true) : undefined}
          primaryColor={primaryColor}
        />
      )}
    </View>
  )
}

function ImageChoiceSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const {t: l} = useLingui()
  const opts = slide.options ?? []
  const [selected, setSelected] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const pick = (id: string, correct: boolean) => {
    if (done) return
    setSelected(id)
    setDone(true)
    onAnswer({
      isCorrect: correct,
      points: correct ? (slide.points ?? 1) : 0,
      timeSpentMs: 0,
      userAnswer: id,
    })
  }
  const isRight = done && !!opts.find(o => o.id === selected && o.isCorrect)
  const correctOpt = opts.find(o => o.isCorrect)
  return (
    <View style={ss.content}>
      <Text style={ss.question}>{slide.question}</Text>
      <View style={ss.imgGrid}>
        {opts.map(o => {
          const isSel = selected === o.id
          const ok = done && o.isCorrect
          const bad = done && isSel && !o.isCorrect
          return (
            <Pressable
              accessibilityRole="button"
              key={o.id}
              onPress={() => pick(o.id, o.isCorrect)}
              style={[
                ss.imgItem,
                {
                  borderColor: ok
                    ? '#22c55e'
                    : bad
                      ? '#ef4444'
                      : isSel
                        ? primaryColor
                        : 'rgba(255,255,255,0.18)',
                  backgroundColor: ok
                    ? '#22c55e18'
                    : bad
                      ? '#ef444418'
                      : 'rgba(255,255,255,0.06)',
                },
              ]}>
              <View
                style={[
                  ss.imgPlaceholder,
                  {backgroundColor: primaryColor + '22'},
                ]}>
                {o.imageUrl ? (
                  <Image
                    source={{uri: o.imageUrl}}
                    style={{width: '100%', height: '100%', borderRadius: 12}}
                    resizeMode="cover"
                    accessibilityIgnoresInvertColors
                  />
                ) : (
                  <Feather name="image" size={28} color={primaryColor + '88'} />
                )}
              </View>
              <Text style={ss.imgLabel} numberOfLines={2}>
                {o.label}
              </Text>
              <View style={ss.imgBadge}>
                <Feather
                  name={ok ? 'check-circle' : bad ? 'x-circle' : 'circle'}
                  size={16}
                  color={
                    ok ? '#22c55e' : bad ? '#ef4444' : 'rgba(255,255,255,0.18)'
                  }
                />
              </View>
            </Pressable>
          )
        })}
      </View>
      {done && (
        <FeedbackBar
          state={isRight ? 'correct' : 'wrong'}
          points={isRight ? (slide.points ?? 1) : 0}
          maxPoints={slide.points ?? 1}
          explanation={slide.explanation}
          solutionText={
            !isRight ? l`Correct answer: ${correctOpt?.label}` : undefined
          }
          onNext={onNext}
          primaryColor={primaryColor}
        />
      )}
    </View>
  )
}

// ─── Accordion Slide ──────────────────────────────────────────────────────────
function AccordionSlide({
  slide,
  primaryColor,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onNext: () => void
}) {
  const items = slide.accordionItems ?? []
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (id: string) =>
    setExpanded(p => {
      const n = new Set(p)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  return (
    <ScrollView
      style={{flex: 1}}
      contentContainerStyle={{padding: 20, gap: 14, paddingBottom: 40}}>
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}
      <View style={{gap: 8}}>
        {items.map(item => {
          const isExp = expanded.has(item.id)
          return (
            <View
              key={item.id}
              style={{
                borderRadius: 14,
                borderWidth: 1.5,
                overflow: 'hidden',
                borderColor: isExp ? primaryColor : 'rgba(255,255,255,0.18)',
              }}>
              <Pressable
                accessibilityRole="button"
                onPress={() => toggle(item.id)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 14,
                  backgroundColor: isExp
                    ? primaryColor + '18'
                    : 'rgba(255,255,255,0.05)',
                }}>
                <Text
                  style={{
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: '700',
                    flex: 1,
                  }}>
                  {item.title}
                </Text>
                <Feather
                  name={isExp ? 'chevron-up' : 'chevron-down'}
                  size={18}
                  color={isExp ? primaryColor : 'rgba(255,255,255,0.4)'}
                />
              </Pressable>
              {isExp && (
                <View
                  style={{
                    padding: 14,
                    backgroundColor: primaryColor + '0a',
                    borderTopWidth: 1,
                    borderTopColor: primaryColor + '33',
                  }}>
                  <Text
                    style={{
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: 14,
                      lineHeight: 22,
                    }}>
                    {item.body}
                  </Text>
                </View>
              )}
            </View>
          )
        })}
      </View>
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onNext}
        style={[ss.primaryBtn, {backgroundColor: primaryColor, marginTop: 4}]}>
        <Text style={ss.primaryBtnTxt}>Continue</Text>
        <Feather name="arrow-right" size={16} color="#fff" />
      </TouchableOpacity>
    </ScrollView>
  )
}

// ─── Flash Content Block Slides ──────────────────────────────────────────────
// Non-exercise slides (text, image, table, chart, formula, drawing).
// All render the content and a single Continue button — no scoring.

function TextBlockSlide({
  slide,
  primaryColor,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onNext: () => void
}) {
  return (
    <View style={ss.content}>
      {!!slide.contentTitle && (
        <Text style={ss.contentH2}>{slide.contentTitle}</Text>
      )}
      {!!slide.contentText && (
        <Text style={ss.contentBody}>{slide.contentText}</Text>
      )}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onNext}
        style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
        <Text style={ss.primaryBtnTxt}>Continue</Text>
        <Feather name="arrow-right" size={16} color="#fff" />
      </TouchableOpacity>
    </View>
  )
}

function ImageBlockSlide({
  slide,
  primaryColor,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onNext: () => void
}) {
  return (
    <View style={ss.content}>
      {!!slide.contentTitle && (
        <Text style={ss.contentH2}>{slide.contentTitle}</Text>
      )}
      {slide.imageUrl ? (
        <View
          style={{borderRadius: 16, overflow: 'hidden', marginVertical: 12}}>
          <Image
            source={{uri: slide.imageUrl}}
            style={{width: '100%', height: 220}}
            resizeMode="cover"
            accessibilityIgnoresInvertColors
          />
        </View>
      ) : (
        <View
          style={{
            height: 160,
            borderRadius: 16,
            borderWidth: 1.5,
            borderColor: 'rgba(255,255,255,0.2)',
            alignItems: 'center',
            justifyContent: 'center',
            marginVertical: 12,
            gap: 6,
          }}>
          <Feather name="image" size={32} color="rgba(255,255,255,0.25)" />
          <Text style={{color: 'rgba(255,255,255,0.25)', fontSize: 13}}>
            No image
          </Text>
        </View>
      )}
      {!!slide.contentText && (
        <Text style={ss.contentBody}>{slide.contentText}</Text>
      )}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onNext}
        style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
        <Text style={ss.primaryBtnTxt}>Continue</Text>
        <Feather name="arrow-right" size={16} color="#fff" />
      </TouchableOpacity>
    </View>
  )
}

function TableBlockSlide({
  slide,
  primaryColor,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onNext: () => void
}) {
  const rows = slide.tableRows ?? []
  const cols = slide.tableCols ?? rows[0]?.length ?? 0
  return (
    <View style={ss.content}>
      {!!slide.contentTitle && (
        <Text style={ss.contentH2}>{slide.contentTitle}</Text>
      )}
      {rows.length > 0 ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator
          style={{marginVertical: 12}}>
          <View
            style={{
              borderRadius: 12,
              overflow: 'hidden',
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.15)',
            }}>
            {rows.map((row, ri) => (
              <View
                key={ri}
                style={{
                  flexDirection: 'row',
                  backgroundColor:
                    ri === 0
                      ? 'rgba(255,255,255,0.12)'
                      : ri % 2 === 0
                        ? 'rgba(255,255,255,0.04)'
                        : 'transparent',
                }}>
                {Array.from({length: Math.max(cols, row.length)}).map(
                  (_, ci) => (
                    <View
                      key={ci}
                      style={{
                        minWidth: 100,
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRightWidth: ci < cols - 1 ? 1 : 0,
                        borderColor: 'rgba(255,255,255,0.1)',
                      }}>
                      <Text
                        style={{
                          color: ri === 0 ? '#fff' : 'rgba(255,255,255,0.8)',
                          fontSize: 13,
                          fontWeight: ri === 0 ? '700' : '400',
                        }}>
                        {row[ci] ?? ''}
                      </Text>
                    </View>
                  ),
                )}
              </View>
            ))}
          </View>
        </ScrollView>
      ) : (
        <View
          style={{
            padding: 16,
            borderRadius: 12,
            borderWidth: 1.5,
            borderColor: 'rgba(255,255,255,0.2)',
            alignItems: 'center',
            marginVertical: 12,
            gap: 6,
          }}>
          <Feather name="grid" size={28} color="rgba(255,255,255,0.25)" />
          <Text style={{color: 'rgba(255,255,255,0.25)', fontSize: 13}}>
            No table data
          </Text>
        </View>
      )}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onNext}
        style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
        <Text style={ss.primaryBtnTxt}>Continue</Text>
        <Feather name="arrow-right" size={16} color="#fff" />
      </TouchableOpacity>
    </View>
  )
}

function ChartBlockSlide({
  slide,
  primaryColor,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onNext: () => void
}) {
  const data = slide.chartData ?? []
  const maxVal = Math.max(...data.map(d => d.value), 1)
  return (
    <View style={ss.content}>
      {!!slide.contentTitle && (
        <Text style={ss.contentH2}>{slide.contentTitle}</Text>
      )}
      {data.length > 0 ? (
        <View style={{marginVertical: 12, gap: 10}}>
          {data.map((d, i) => (
            <View key={i} style={{gap: 4}}>
              <Text style={{color: 'rgba(255,255,255,0.7)', fontSize: 12}}>
                {d.label}
              </Text>
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                <View
                  style={{
                    flex: 1,
                    height: 28,
                    borderRadius: 6,
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    overflow: 'hidden',
                  }}>
                  <View
                    style={{
                      width: `${Math.round((d.value / maxVal) * 100)}%`,
                      height: '100%',
                      backgroundColor: primaryColor + 'cc',
                      borderRadius: 6,
                    }}
                  />
                </View>
                <Text
                  style={{
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: '700',
                    minWidth: 36,
                    textAlign: 'right',
                  }}>
                  {d.value}
                </Text>
              </View>
            </View>
          ))}
        </View>
      ) : (
        <View
          style={{
            padding: 16,
            borderRadius: 12,
            borderWidth: 1.5,
            borderColor: 'rgba(255,255,255,0.2)',
            alignItems: 'center',
            marginVertical: 12,
            gap: 6,
          }}>
          <Feather
            name="bar-chart-2"
            size={28}
            color="rgba(255,255,255,0.25)"
          />
          <Text style={{color: 'rgba(255,255,255,0.25)', fontSize: 13}}>
            No chart data
          </Text>
        </View>
      )}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onNext}
        style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
        <Text style={ss.primaryBtnTxt}>Continue</Text>
        <Feather name="arrow-right" size={16} color="#fff" />
      </TouchableOpacity>
    </View>
  )
}

function FormulaBlockSlide({
  slide,
  primaryColor,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onNext: () => void
}) {
  return (
    <View style={ss.content}>
      {!!slide.contentTitle && (
        <Text style={ss.contentH2}>{slide.contentTitle}</Text>
      )}
      {slide.latex ? (
        <View
          style={{
            padding: 20,
            borderRadius: 14,
            backgroundColor: 'rgba(139,92,246,0.12)',
            borderWidth: 1.5,
            borderColor: 'rgba(139,92,246,0.35)',
            marginVertical: 12,
          }}>
          <Text
            style={{
              color: '#c4b5fd',
              fontSize: 18,
              fontFamily: 'monospace',
              textAlign: 'center',
              lineHeight: 28,
            }}>
            {slide.latex}
          </Text>
        </View>
      ) : null}
      {!!slide.contentText && (
        <Text style={ss.contentBody}>{slide.contentText}</Text>
      )}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onNext}
        style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
        <Text style={ss.primaryBtnTxt}>Continue</Text>
        <Feather name="arrow-right" size={16} color="#fff" />
      </TouchableOpacity>
    </View>
  )
}

function DrawingBlockSlide({
  slide,
  primaryColor,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onNext: () => void
}) {
  return (
    <View style={ss.content}>
      {!!slide.contentTitle && (
        <Text style={ss.contentH2}>{slide.contentTitle}</Text>
      )}
      <View
        style={{
          height: 200,
          borderRadius: 16,
          borderWidth: 1.5,
          borderColor: 'rgba(236,72,153,0.4)',
          backgroundColor: 'rgba(236,72,153,0.06)',
          alignItems: 'center',
          justifyContent: 'center',
          marginVertical: 12,
          gap: 8,
        }}>
        <Feather name="edit-2" size={32} color="rgba(236,72,153,0.5)" />
        <Text style={{color: 'rgba(255,255,255,0.35)', fontSize: 13}}>
          Drawing — web only
        </Text>
      </View>
      {!!slide.contentText && (
        <Text style={ss.contentBody}>{slide.contentText}</Text>
      )}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onNext}
        style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
        <Text style={ss.primaryBtnTxt}>Continue</Text>
        <Feather name="arrow-right" size={16} color="#fff" />
      </TouchableOpacity>
    </View>
  )
}

// ─── Drag Words Slide ─────────────────────────────────────────────────────────
function DraggableWordChip({
  word,
  disabled,
  isSelected,
  primaryColor,
  onTap,
  onDrop,
}: {
  word: string
  disabled: boolean
  isSelected: boolean
  primaryColor: string
  onTap: () => void
  onDrop: (pageX: number, pageY: number) => void
}) {
  const [pan] = useState(() => new Animated.ValueXY())
  const [dragging, setDragging] = useState(false)
  // Track whether the touch moved enough to count as a drag vs a tap
  const dragStarted = useRef(false)
  const panResponder = useRef(
    PanResponder.create({
      // Claim the responder immediately so the chip owns the gesture from the
      // first touch, preventing the ScrollView from intercepting it.
      onStartShouldSetPanResponder: () => !disabled,
      onStartShouldSetPanResponderCapture: () => !disabled,
      onMoveShouldSetPanResponder: () => !disabled,
      onPanResponderGrant: () => {
        dragStarted.current = false
        pan.setValue({x: 0, y: 0})
      },
      onPanResponderMove: (_, g) => {
        // Only start visual dragging after moving 6px to distinguish from taps
        if (!dragStarted.current && Math.hypot(g.dx, g.dy) > 6) {
          dragStarted.current = true
          setDragging(true)
        }
        if (dragStarted.current) {
          pan.setValue({x: g.dx, y: g.dy})
        }
      },
      onPanResponderRelease: (_, g) => {
        if (dragStarted.current) {
          // It was a drag: attempt to drop onto a blank
          onDrop(g.moveX, g.moveY)
        } else {
          // It was a tap: delegate to onTap
          onTap()
        }
        dragStarted.current = false
        setDragging(false)
        Animated.spring(pan, {
          toValue: {x: 0, y: 0},
          useNativeDriver: false,
          friction: 6,
        }).start()
      },
      onPanResponderTerminate: () => {
        dragStarted.current = false
        setDragging(false)
        Animated.spring(pan, {
          toValue: {x: 0, y: 0},
          useNativeDriver: false,
          friction: 6,
        }).start()
      },
    }),
  ).current

  return (
    <Animated.View
      accessibilityRole="button"
      {...panResponder.panHandlers}
      style={{
        transform: pan.getTranslateTransform(),
        zIndex: dragging ? 10 : 1,
        opacity: disabled ? 0.3 : 1,
      }}>
      <View
        style={{
          paddingHorizontal: 12,
          paddingVertical: 7,
          borderRadius: 20,
          borderWidth: 1.5,
          borderColor: isSelected ? primaryColor : 'rgba(255,255,255,0.25)',
          backgroundColor: isSelected
            ? primaryColor + '33'
            : 'rgba(255,255,255,0.08)',
          shadowColor: '#000',
          shadowOpacity: dragging ? 0.4 : 0,
          shadowRadius: 8,
          shadowOffset: {width: 0, height: 4},
          elevation: dragging ? 6 : 0,
        }}>
        <Text
          style={{
            color: isSelected ? primaryColor : '#fff',
            fontSize: 14,
            fontWeight: '600',
          }}>
          {word}
        </Text>
      </View>
    </Animated.View>
  )
}

function DragWordsSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const template = slide.dragWordsTemplate ?? ''
  const templateParts = template.split(/(\[[^\]]+\])/)
  const blanks = templateParts
    .filter(p => /^\[.+\]$/.test(p))
    .map(p => p.slice(1, -1))
  const allWords = useRef(
    [...blanks, ...(slide.dragWordsBank ?? [])].sort(() => Math.random() - 0.5),
  ).current
  const [fills, setFills] = useState<(string | null)[]>(
    Array(blanks.length).fill(null),
  )
  const [selectedWord, setSelectedWord] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const blankRefs = useRef<Record<number, View | null>>({})
  let blankIdx = 0
  const fillBlank = (idx: number) => {
    if (checked) return
    if (selectedWord) {
      setFills(f => {
        const n = [...f]
        n[idx] = selectedWord
        return n
      })
      setSelectedWord(null)
    } else if (fills[idx]) {
      setSelectedWord(fills[idx])
      setFills(f => {
        const n = [...f]
        n[idx] = null
        return n
      })
    }
  }
  const placeWordInBlank = (idx: number, word: string) => {
    if (checked) return
    setFills(f => {
      const n = [...f]
      n[idx] = word
      return n
    })
    setSelectedWord(null)
  }
  const measureBlank = (
    idx: number,
  ): Promise<{x: number; y: number; width: number; height: number} | null> =>
    new Promise(resolve => {
      const node = blankRefs.current[idx]
      if (!node) {
        resolve(null)
        return
      }
      // @ts-ignore RN View has measureInWindow at runtime
      node.measureInWindow(
        (x: number, y: number, width: number, height: number) =>
          resolve({x, y, width, height}),
      )
    })
  const tryDrop = async (word: string, pageX: number, pageY: number) => {
    if (checked) return
    for (let idx = 0; idx < blanks.length; idx++) {
      const rect = await measureBlank(idx)
      if (
        rect &&
        pageX >= rect.x &&
        pageX <= rect.x + rect.width &&
        pageY >= rect.y &&
        pageY <= rect.y + rect.height
      ) {
        placeWordInBlank(idx, word)
        return
      }
    }
  }
  const allFilled = fills.every(f => f !== null)
  const maxPts = slide.points ?? blanks.length
  const okCount = checked
    ? fills.filter(
        (f, i) => f?.toLowerCase().trim() === blanks[i]?.toLowerCase().trim(),
      ).length
    : 0
  const pts = blanks.length ? Math.round((okCount / blanks.length) * maxPts) : 0
  const fbState: FBState = showSol
    ? 'solution'
    : okCount === blanks.length
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'
  const verify = () => {
    onAnswer({
      isCorrect: okCount === blanks.length,
      points: pts,
      timeSpentMs: 0,
      userAnswer: fills,
    })
    setChecked(true)
  }
  const retry = () => {
    setFills(Array(blanks.length).fill(null))
    setSelectedWord(null)
    setChecked(false)
    setShowSol(false)
    setRetried(true)
  }
  // usedWords was used for display elsewhere in the reference; not needed here.
  const displayFills = showSol ? blanks : fills
  const DW_ACCENT = SLIDE_ACCENT.drag_words
  return (
    <ScrollView
      style={{flex: 1}}
      contentContainerStyle={{padding: 20, gap: 16, paddingBottom: 40}}
      keyboardShouldPersistTaps="handled">
      <TypeBadge type="drag_words" />
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}

      {/* Paragraph card with inline blank slots */}
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 4,
          rowGap: 8,
          alignItems: 'center',
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 14,
          padding: 16,
          borderWidth: 1,
          borderColor: 'rgba(255,255,255,0.1)',
        }}>
        {templateParts.map((part, pi) => {
          if (/^\[.+\]$/.test(part)) {
            const bi = blankIdx++
            const fill = displayFills[bi]
            const okF =
              checked &&
              !showSol &&
              fill?.toLowerCase().trim() === blanks[bi]?.toLowerCase().trim()
            const badF = checked && !showSol && !okF
            return (
              <TouchableOpacity
                accessibilityRole="button"
                key={pi}
                onPress={() => fillBlank(bi)}
                disabled={showSol}
                ref={el => {
                  blankRefs.current[bi] = el
                }}
                style={{
                  minWidth: 80,
                  height: 36,
                  borderRadius: 8,
                  borderWidth: 1.5,
                  paddingHorizontal: 10,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderStyle: fill ? 'solid' : 'dashed',
                  borderColor: showSol
                    ? DW_ACCENT
                    : okF
                      ? '#22c55e'
                      : badF
                        ? '#ef4444'
                        : selectedWord
                          ? DW_ACCENT
                          : fill
                            ? DW_ACCENT + '99'
                            : DW_ACCENT + '66',
                  backgroundColor: showSol
                    ? DW_ACCENT + '18'
                    : okF
                      ? '#22c55e18'
                      : badF
                        ? '#ef444418'
                        : fill
                          ? DW_ACCENT + '18'
                          : 'rgba(255,255,255,0.04)',
                }}>
                <Text
                  style={{
                    color: showSol
                      ? DW_ACCENT
                      : okF
                        ? '#22c55e'
                        : badF
                          ? '#ef4444'
                          : fill
                            ? '#fff'
                            : DW_ACCENT + 'aa',
                    fontSize: 13,
                    fontWeight: '700',
                  }}>
                  {fill ?? '   '}
                </Text>
              </TouchableOpacity>
            )
          }
          return part
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((word, wi) => (
              <Text
                key={'t' + pi + wi}
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: 15,
                  lineHeight: 22,
                }}>
                {word}
              </Text>
            ))
        })}
      </View>

      {/* Word bank */}
      {!checked && (
        <View style={{gap: 8}}>
          <Text
            style={{
              color: PLAY_SURFACE.textFaint,
              fontSize: 10,
              fontWeight: '700',
              letterSpacing: 0.9,
            }}>
            WORD BANK
          </Text>
          <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 8}}>
            {allWords.map((word, wi) => {
              const timesInBlanks = fills.filter(f => f === word).length
              const isUsed =
                timesInBlanks >= allWords.filter(w => w === word).length
              const isSel = word === selectedWord
              return (
                <DraggableWordChip
                  key={wi}
                  word={word}
                  disabled={isUsed && !isSel}
                  isSelected={isSel}
                  primaryColor={DW_ACCENT}
                  onTap={() => {
                    if (isUsed && !isSel) return
                    setSelectedWord(isSel ? null : word)
                  }}
                  onDrop={(pageX, pageY) => {
                    if (isUsed && !isSel) return
                    tryDrop(word, pageX, pageY)
                  }}
                />
              )
            })}
          </View>
          {selectedWord && (
            <Text
              style={{
                color: DW_ACCENT,
                fontSize: 12,
                textAlign: 'center',
                fontWeight: '600',
              }}>
              "{selectedWord}" selected — tap a blank to place it
            </Text>
          )}
        </View>
      )}

      {!checked && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          disabled={!allFilled}
          style={[
            ss.verifyBtn,
            {
              backgroundColor: allFilled ? DW_ACCENT : 'rgba(255,255,255,0.1)',
              opacity: allFilled ? 1 : 0.5,
            },
          ]}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={ss.verifyTxt}>Check</Text>
        </TouchableOpacity>
      )}
      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={blanks.join(' · ')}
          onNext={onNext}
          onRetry={!retried ? retry : undefined}
          onShowSolution={
            !showSol
              ? () => {
                  setShowSol(true)
                  setChecked(true)
                }
              : undefined
          }
          primaryColor={primaryColor}
        />
      )}
    </ScrollView>
  )
}

// ─── Fill Blank Slide (blanks[] format) ───────────────────────────────────────
function FillBlankSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const blanks = slide.blanks ?? []
  const [inputs, setInputs] = useState<string[]>(Array(blanks.length).fill(''))
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)

  const isBlankCorrect = (i: number) => {
    const blank = blanks[i]
    if (!blank) return false
    const raw = (inputs[i] ?? '').trim().toLowerCase()
    const accepted = [blank.answer, ...(blank.alternatives ?? [])].map(a =>
      a.trim().toLowerCase(),
    )
    return accepted.includes(raw)
  }

  const results = blanks.map((_, i) => isBlankCorrect(i))
  const okCount = results.filter(Boolean).length
  const maxPts = slide.points ?? blanks.length
  const pts = blanks.length ? Math.round((okCount / blanks.length) * maxPts) : 0
  const allFilled = inputs.every(v => v.trim().length > 0)

  const verify = () => {
    onAnswer({
      isCorrect: okCount === blanks.length,
      points: pts,
      timeSpentMs: 0,
      userAnswer: inputs,
    })
    setChecked(true)
  }

  const retry = () => {
    setInputs(Array(blanks.length).fill(''))
    setChecked(false)
    setRetried(true)
    setShowSol(false)
  }

  const fbState: FBState = showSol
    ? 'solution'
    : okCount === blanks.length
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'

  const segments: Array<{text: string} | {blankIdx: number}> = []
  if (blanks.length > 0) {
    if (blanks[0].before) segments.push({text: blanks[0].before})
    blanks.forEach((blank, i) => {
      segments.push({blankIdx: i})
      if (blank.after) segments.push({text: blank.after})
    })
  }

  return (
    <ScrollView
      style={{flex: 1}}
      contentContainerStyle={{padding: 20, gap: 14, paddingBottom: 40}}
      keyboardShouldPersistTaps="handled">
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}
      <View
        style={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 14,
          padding: 16,
          borderWidth: 1,
          borderColor: 'rgba(255,255,255,0.1)',
        }}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 4,
            rowGap: 8,
          }}>
          {segments.map((seg, si) => {
            if ('text' in seg) {
              return seg.text
                .split(/\s+/)
                .filter(Boolean)
                .map((word, wi) => (
                  <Text
                    key={`${si}-${wi}`}
                    style={{
                      color: 'rgba(255,255,255,0.85)',
                      fontSize: 15,
                      lineHeight: 22,
                    }}>
                    {word}
                  </Text>
                ))
            }
            const i = seg.blankIdx
            const blank = blanks[i]
            const okF = checked && !showSol && results[i]
            const badF = checked && !showSol && !results[i]
            const bc = showSol
              ? '#d97706'
              : okF
                ? '#22c55e'
                : badF
                  ? '#ef4444'
                  : primaryColor + '80'
            const inputW = Math.max(blank.answer.length * 10 + 16, 52)
            return (
              <Fragment key={`blank-${i}`}>
                <View
                  style={{flexDirection: 'row', alignItems: 'center', gap: 3}}>
                  <TextInput
                    accessibilityLabel="Text input field"
                    accessibilityHint="Type your answer in the blank"
                    style={{
                      width: inputW,
                      height: 32,
                      paddingHorizontal: 8,
                      borderRadius: 7,
                      borderWidth: 1.5,
                      fontSize: 13,
                      textAlign: 'center',
                      color: showSol ? '#d97706' : '#fff',
                      backgroundColor: showSol
                        ? '#d9770611'
                        : 'rgba(255,255,255,0.07)',
                      borderColor: bc,
                    }}
                    placeholder="…"
                    placeholderTextColor="rgba(255,255,255,0.3)"
                    autoCapitalize="none"
                    autoCorrect={false}
                    value={showSol ? blank.answer : (inputs[i] ?? '')}
                    onChangeText={val =>
                      !checked &&
                      !showSol &&
                      setInputs(prev => {
                        const next = [...prev]
                        next[i] = val
                        return next
                      })
                    }
                    editable={!checked && !showSol}
                  />
                  {checked && !showSol && (
                    <Feather
                      name={results[i] ? 'check-circle' : 'x-circle'}
                      size={14}
                      color={results[i] ? '#22c55e' : '#ef4444'}
                    />
                  )}
                </View>
              </Fragment>
            )
          })}
        </View>
      </View>
      {!checked && !showSol && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          disabled={!allFilled}
          style={[
            ss.verifyBtn,
            {
              backgroundColor: allFilled
                ? primaryColor
                : 'rgba(255,255,255,0.1)',
              opacity: allFilled ? 1 : 0.5,
            },
          ]}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={ss.verifyTxt}>Check</Text>
        </TouchableOpacity>
      )}
      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={blanks.map(b => b.answer).join(' · ')}
          onNext={onNext}
          onRetry={!retried ? retry : undefined}
          onShowSolution={
            !showSol
              ? () => {
                  setShowSol(true)
                  setChecked(true)
                }
              : undefined
          }
          primaryColor={primaryColor}
        />
      )}
    </ScrollView>
  )
}

// ─── Fill Blanks Slide (inline paragraph) ─────────────────────────────────────
function FillBlanksSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const paragraph = slide.fillBlanksParagraph ?? ''
  const answers = slide.fillBlanksAnswers ?? []
  const parts = paragraph.split('___')
  const [vals, setVals] = useState<string[]>(Array(answers.length).fill(''))
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const checkA = (a: (typeof answers)[0], v: string) => {
    const u = v.toLowerCase().trim()
    return (
      u === a.answer.toLowerCase().trim() ||
      (a.alternatives ?? []).some(x => x.toLowerCase().trim() === u)
    )
  }
  const results = answers.map((a, i) => checkA(a, vals[i] ?? ''))
  const okCount = results.filter(Boolean).length
  const maxPts = slide.points ?? answers.length
  const pts = answers.length
    ? Math.round((okCount / answers.length) * maxPts)
    : 0
  const allFilled = vals.every(v => v.trim().length > 0)
  const verify = () => {
    onAnswer({
      isCorrect: okCount === answers.length,
      points: pts,
      timeSpentMs: 0,
      userAnswer: vals,
    })
    setChecked(true)
  }
  const retry = () => {
    setVals(Array(answers.length).fill(''))
    setChecked(false)
    setRetried(true)
  }
  const fbState: FBState = showSol
    ? 'solution'
    : okCount === answers.length
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'
  return (
    <ScrollView
      style={{flex: 1}}
      contentContainerStyle={{padding: 20, gap: 14, paddingBottom: 40}}
      keyboardShouldPersistTaps="handled">
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}
      <View
        style={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 14,
          padding: 16,
          borderWidth: 1,
          borderColor: 'rgba(255,255,255,0.1)',
        }}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 4,
            rowGap: 8,
          }}>
          {parts.map((part, i) => {
            const okF = checked && !showSol && results[i]
            const badF = checked && !showSol && !results[i]
            const bc = showSol
              ? '#d97706'
              : okF
                ? '#22c55e'
                : badF
                  ? '#ef4444'
                  : primaryColor + '80'
            const a = answers[i]
            const inputW = a ? Math.max(a.answer.length * 10 + 16, 52) : 0
            return (
              <Fragment key={i}>
                {part
                  .trim()
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((word, wi) => (
                    <Text
                      key={`p${i}w${wi}`}
                      style={{
                        color: 'rgba(255,255,255,0.85)',
                        fontSize: 15,
                        lineHeight: 22,
                      }}>
                      {word}
                    </Text>
                  ))}
                {i < answers.length && (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 3,
                    }}>
                    <TextInput
                      accessibilityLabel="Text input field"
                      accessibilityHint="Type your answer in the blank"
                      style={{
                        width: inputW,
                        height: 32,
                        paddingHorizontal: 8,
                        borderRadius: 7,
                        borderWidth: 1.5,
                        fontSize: 13,
                        textAlign: 'center',
                        color: showSol ? '#d97706' : '#fff',
                        backgroundColor: showSol
                          ? '#d9770611'
                          : 'rgba(255,255,255,0.07)',
                        borderColor: bc,
                      }}
                      placeholder="…"
                      placeholderTextColor="rgba(255,255,255,0.3)"
                      autoCapitalize="none"
                      autoCorrect={false}
                      value={showSol ? (a?.answer ?? '') : (vals[i] ?? '')}
                      onChangeText={t =>
                        !checked &&
                        !showSol &&
                        setVals(v => {
                          const n = [...v]
                          n[i] = t
                          return n
                        })
                      }
                      editable={!checked && !showSol}
                    />
                    {checked && !showSol && (
                      <Feather
                        name={results[i] ? 'check-circle' : 'x-circle'}
                        size={14}
                        color={results[i] ? '#22c55e' : '#ef4444'}
                      />
                    )}
                  </View>
                )}
              </Fragment>
            )
          })}
        </View>
      </View>
      {!checked && !showSol && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          disabled={!allFilled}
          style={[
            ss.verifyBtn,
            {
              backgroundColor: allFilled
                ? primaryColor
                : 'rgba(255,255,255,0.1)',
              opacity: allFilled ? 1 : 0.5,
            },
          ]}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={ss.verifyTxt}>Check</Text>
        </TouchableOpacity>
      )}
      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={answers.map(a => a.answer).join(' · ')}
          onNext={onNext}
          onRetry={!retried ? retry : undefined}
          onShowSolution={
            !showSol
              ? () => {
                  setShowSol(true)
                  setChecked(true)
                }
              : undefined
          }
          primaryColor={primaryColor}
        />
      )}
    </ScrollView>
  )
}

// ─── Find Words Slide ─────────────────────────────────────────────────────────
// Timer budget is proportional to the number of words the student must find,
// so a 5-word grid gets less time than a 15-word grid. Tune these two
// constants to change the difficulty curve without touching any other logic.
const FIND_WORDS_SECONDS_PER_WORD = 12 // seconds of time granted per target word
const FIND_WORDS_MIN_SECONDS = 30 // floor, so tiny word lists still feel fair

function locateWordInGrid(
  grid: string[][],
  word: string,
): {r: number; c: number}[] | null {
  const rows = grid.length
  const cols = grid[0]?.length ?? 0
  const dirs: [number, number][] = [
    [0, 1],
    [0, -1],
    [1, 0],
    [-1, 0],
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ]
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      for (const [dr, dc] of dirs) {
        let ok = true
        const cells: {r: number; c: number}[] = []
        for (let i = 0; i < word.length; i++) {
          const rr = r + dr * i,
            cc = c + dc * i
          if (
            rr < 0 ||
            rr >= rows ||
            cc < 0 ||
            cc >= cols ||
            grid[rr]?.[cc] !== word[i]
          ) {
            ok = false
            break
          }
          cells.push({r: rr, c: cc})
        }
        if (ok) return cells
      }
    }
  }
  return null
}

function formatTime(totalSeconds: number) {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function FindWordsSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const {t: l} = useLingui()
  const grid = slide.findWordsGrid ?? []
  const targets = (slide.findWordsTargets ?? []).map(w => w.toUpperCase())
  const cols = grid[0]?.length ?? 8
  const CELL = Math.min(
    34,
    Math.max(Math.floor((Dimensions.get('window').width - 40) / cols), 22),
  )
  const totalSeconds = Math.max(
    targets.length * FIND_WORDS_SECONDS_PER_WORD,
    FIND_WORDS_MIN_SECONDS,
  )
  const [startCell, setStartCell] = useState<{r: number; c: number} | null>(
    null,
  )
  const [foundWords, setFoundWords] = useState<string[]>([])
  const [foundCells, setFoundCells] = useState<Set<string>>(new Set())
  const [submitted, setSubmitted] = useState(false)
  const [timedOut, setTimedOut] = useState(false) // true if ended via timer or skip (not by finding all words)
  const [missedCells, setMissedCells] = useState<Set<string>>(new Set())
  const [secondsLeft, setSecondsLeft] = useState(totalSeconds)
  const ck = (r: number, c: number) => r + ',' + c

  const endExercise = (reason: 'timeout' | 'skip') => {
    if (submitted) return
    const newMissed = new Set<string>()
    targets.forEach(t => {
      if (foundWords.includes(t)) return
      const cells = locateWordInGrid(grid, t)
      cells?.forEach(cell => newMissed.add(ck(cell.r, cell.c)))
    })
    setMissedCells(newMissed)
    setTimedOut(true)
    setSubmitted(true)
    const pts = Math.round(
      (foundWords.length / Math.max(targets.length, 1)) * (slide.points ?? 20),
    )
    onAnswer({
      isCorrect: foundWords.length === targets.length,
      points: pts,
      timeSpentMs: 0,
      userAnswer: foundWords,
    })
    void reason // kept for future analytics/logging use
  }

  useEffect(() => {
    if (submitted) return
    if (secondsLeft <= 0) {
      endExercise('timeout')
      return
    }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [secondsLeft, submitted])

  const getLine = (r1: number, c1: number, r2: number, c2: number) => {
    const dr = r1 === r2 ? 0 : r2 > r1 ? 1 : -1
    const dc = c1 === c2 ? 0 : c2 > c1 ? 1 : -1
    if (r1 !== r2 && c1 !== c2 && Math.abs(r2 - r1) !== Math.abs(c2 - c1))
      return null
    const len = Math.max(Math.abs(r2 - r1), Math.abs(c2 - c1))
    const cells: {r: number; c: number}[] = []
    for (let i = 0; i <= len; i++) cells.push({r: r1 + i * dr, c: c1 + i * dc})
    return cells
  }
  const handleCell = (r: number, c: number) => {
    if (submitted) return
    if (!startCell) {
      setStartCell({r, c})
      return
    }
    if (startCell.r === r && startCell.c === c) {
      setStartCell(null)
      return
    }
    const line = getLine(startCell.r, startCell.c, r, c)
    if (!line) {
      setStartCell({r, c})
      return
    }
    const word = line.map(cell => grid[cell.r]?.[cell.c] ?? '').join('')
    const wordRev = word.split('').reverse().join('')
    const found = targets.find(
      t => !foundWords.includes(t) && (t === word || t === wordRev),
    )
    if (found) {
      const newFC = new Set(foundCells)
      line.forEach(cell => newFC.add(ck(cell.r, cell.c)))
      setFoundCells(newFC)
      const newFW = [...foundWords, found]
      setFoundWords(newFW)
      if (newFW.length >= targets.length) {
        onAnswer({
          isCorrect: true,
          points: slide.points ?? 20,
          timeSpentMs: 0,
          userAnswer: newFW,
        })
        setSubmitted(true)
      }
    }
    setStartCell(null)
  }
  if (!grid.length)
    return (
      <View style={ss.content}>
        <Text
          style={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: 14,
            textAlign: 'center',
            marginTop: 20,
          }}>
          {l`Grid not generated — edit the capsule to generate the grid.`}
        </Text>
      </View>
    )
  const urgent = secondsLeft <= 10 && !submitted
  return (
    <ScrollView
      style={{flex: 1}}
      contentContainerStyle={{padding: 20, gap: 14, paddingBottom: 40}}>
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}
      {!submitted && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 12,
            paddingHorizontal: 12,
            paddingVertical: 8,
            borderWidth: 1,
            borderColor: urgent ? '#ef444455' : 'rgba(255,255,255,0.1)',
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
            <Feather
              name="clock"
              size={14}
              color={urgent ? '#ef4444' : 'rgba(255,255,255,0.6)'}
            />
            <Text
              style={{
                color: urgent ? '#ef4444' : '#fff',
                fontSize: 14,
                fontWeight: '800',
              }}>
              {formatTime(secondsLeft)}
            </Text>
          </View>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => endExercise('skip')}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
              paddingHorizontal: 10,
              paddingVertical: 5,
              borderRadius: 20,
              backgroundColor: 'rgba(255,255,255,0.08)',
            }}>
            <Feather
              name="skip-forward"
              size={12}
              color="rgba(255,255,255,0.7)"
            />
            <Text
              style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: 12,
                fontWeight: '700',
              }}>
              Skip
            </Text>
          </TouchableOpacity>
        </View>
      )}
      <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 8}}>
        {targets.map(word => {
          const isF = foundWords.includes(word)
          const isMissed = timedOut && !isF
          return (
            <View
              key={word}
              style={{
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 20,
                borderWidth: 1.5,
                borderColor: isF
                  ? '#10b981'
                  : isMissed
                    ? '#f97316'
                    : 'rgba(255,255,255,0.25)',
                backgroundColor: isF
                  ? '#10b98122'
                  : isMissed
                    ? '#f9731622'
                    : 'rgba(255,255,255,0.06)',
              }}>
              <Text
                style={{
                  color: isF ? '#10b981' : isMissed ? '#f97316' : '#fff',
                  fontSize: 13,
                  fontWeight: '700',
                  textDecorationLine: isF ? 'line-through' : 'none',
                }}>
                {word}
              </Text>
            </View>
          )
        })}
      </View>
      <View
        style={{
          alignSelf: 'center',
          borderRadius: 12,
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: 'rgba(255,255,255,0.1)',
        }}>
        {grid.map((row, r) => (
          <View key={r} style={{flexDirection: 'row'}}>
            {row.map((letter, c) => {
              const isSC = startCell?.r === r && startCell?.c === c
              const isFC = foundCells.has(ck(r, c))
              const isMC = missedCells.has(ck(r, c))
              return (
                <TouchableOpacity
                  accessibilityRole="button"
                  key={c}
                  onPress={() => handleCell(r, c)}
                  style={{
                    width: CELL,
                    height: CELL,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isFC
                      ? '#10b98133'
                      : isMC
                        ? '#f9731633'
                        : isSC
                          ? primaryColor + '44'
                          : 'rgba(255,255,255,0.03)',
                    borderWidth: 0.5,
                    borderColor: 'rgba(255,255,255,0.08)',
                  }}>
                  <Text
                    style={{
                      color: isFC
                        ? '#10b981'
                        : isMC
                          ? '#f97316'
                          : isSC
                            ? primaryColor
                            : 'rgba(255,255,255,0.85)',
                      fontSize: Math.max(CELL - 12, 10),
                      fontWeight: isFC || isMC ? '800' : '500',
                    }}>
                    {letter}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </View>
        ))}
      </View>
      {submitted && (
        <FeedbackBar
          state={
            timedOut ? (foundWords.length > 0 ? 'partial' : 'wrong') : 'correct'
          }
          points={Math.round(
            (foundWords.length / Math.max(targets.length, 1)) *
              (slide.points ?? 20),
          )}
          maxPoints={slide.points ?? 20}
          explanation={
            timedOut
              ? `${foundWords.length}/${targets.length} words found. Missed words are highlighted in orange above.${slide.explanation ? ' ' + slide.explanation : ''}`
              : slide.explanation
          }
          onNext={onNext}
          primaryColor={primaryColor}
        />
      )}
      {!submitted && (
        <Text
          style={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: 12,
            textAlign: 'center',
          }}>
          {l`Tap the first letter, then the last letter of the word`}
        </Text>
      )}
    </ScrollView>
  )
}

// ─── Mark Words Slide ─────────────────────────────────────────────────────────
function MarkWordsSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const {t: l} = useLingui()
  const text = slide.markWordsText ?? ''
  const correct = (slide.markWordsCorrect ?? []).map(w =>
    w.toLowerCase().trim(),
  )
  const tokens = text.split(/\s+/).filter(Boolean)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [checked, setChecked] = useState(false)
  const toggle = (i: number) => {
    if (checked) return
    setSelected(p => {
      const n = new Set(p)
      n.has(i) ? n.delete(i) : n.add(i)
      return n
    })
  }
  const cleanW = (w: string) =>
    w
      .replace(/[.,!?;:()«»]/g, '')
      .toLowerCase()
      .trim()
  const selWords = [...selected].map(i => cleanW(tokens[i] ?? ''))
  const corrSel = selWords.filter(w => correct.includes(w)).length
  const wrongSel = selWords.filter(w => !correct.includes(w)).length
  const maxPts = slide.points ?? correct.length
  const pts = Math.max(
    0,
    Math.round(((corrSel - wrongSel) / Math.max(correct.length, 1)) * maxPts),
  )
  const allOk = corrSel === correct.length && wrongSel === 0
  const fbState: FBState = allOk ? 'correct' : corrSel > 0 ? 'partial' : 'wrong'
  const verify = () => {
    onAnswer({
      isCorrect: allOk,
      points: pts,
      timeSpentMs: 0,
      userAnswer: [...selected],
    })
    setChecked(true)
  }
  return (
    <ScrollView
      style={{flex: 1}}
      contentContainerStyle={{padding: 20, gap: 14, paddingBottom: 40}}>
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          alignContent: 'flex-start',
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 14,
          padding: 12,
          borderWidth: 1,
          borderColor: 'rgba(255,255,255,0.1)',
        }}>
        {tokens.map((word, i) => {
          const isSel = selected.has(i)
          const cw = cleanW(word)
          const isCorrectW = correct.includes(cw)
          const bg = checked
            ? isSel && isCorrectW
              ? '#22c55e33'
              : isSel && !isCorrectW
                ? '#ef444433'
                : !isSel && isCorrectW
                  ? '#f9731622'
                  : 'transparent'
            : isSel
              ? primaryColor + '33'
              : 'transparent'
          const border = checked
            ? isSel && isCorrectW
              ? '#22c55e'
              : isSel && !isCorrectW
                ? '#ef4444'
                : !isSel && isCorrectW
                  ? '#f97316'
                  : 'transparent'
            : isSel
              ? primaryColor
              : 'transparent'
          return (
            <Pressable
              accessibilityRole="button"
              key={i}
              onPress={() => toggle(i)}
              style={{
                paddingHorizontal: 6,
                paddingVertical: 3,
                borderRadius: 6,
                borderWidth: 1.5,
                borderColor: border,
                backgroundColor: bg,
                marginRight: 2,
                marginBottom: 2,
              }}>
              <Text style={{color: '#fff', fontSize: 15, lineHeight: 22}}>
                {word}
              </Text>
            </Pressable>
          )
        })}
      </View>
      {!checked && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          disabled={selected.size === 0}
          style={[
            ss.verifyBtn,
            {
              backgroundColor:
                selected.size > 0 ? primaryColor : 'rgba(255,255,255,0.1)',
              opacity: selected.size > 0 ? 1 : 0.5,
            },
          ]}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={ss.verifyTxt}>Check</Text>
        </TouchableOpacity>
      )}
      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={slide.explanation}
          solutionText={l`Correct words: ${(slide.markWordsCorrect ?? []).join(', ')}`}
          onNext={onNext}
          primaryColor={primaryColor}
        />
      )}
    </ScrollView>
  )
}

// ─── Complex Fill Blank Slide ─────────────────────────────────────────────────
function ComplexFillBlankSlide({
  slide,
  primaryColor,
  onAnswer,
  onNext,
}: {
  slide: Slide
  primaryColor: string
  onAnswer: (r: AnswerResult) => void
  onNext: () => void
}) {
  const blanks = slide.complexBlanks ?? []
  // Include correct answer shuffled with wrong options (answer is stored separately in this data model)
  const [allOptions] = useState(() =>
    blanks.map(b => [b.answer, ...b.options].sort(() => Math.random() - 0.5)),
  )
  const [selected, setSelected] = useState<(string | null)[]>(
    Array(blanks.length).fill(null),
  )
  const [openPicker, setOpenPicker] = useState<number | null>(null)
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const checkA = (blank: (typeof blanks)[0], val: string | null) => {
    if (!val) return false
    const u = val.toLowerCase().trim()
    return (
      u === blank.answer.toLowerCase().trim() ||
      (blank.alternatives ?? []).some(a => a.toLowerCase().trim() === u)
    )
  }
  const results = blanks.map((b, i) => checkA(b, selected[i]))
  const okCount = results.filter(Boolean).length
  const maxPts = slide.points ?? blanks.length
  const pts = blanks.length ? Math.round((okCount / blanks.length) * maxPts) : 0
  const allFilled = selected.every(sv => sv !== null)
  const fbState: FBState = showSol
    ? 'solution'
    : okCount === blanks.length
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'
  const verify = () => {
    onAnswer({
      isCorrect: okCount === blanks.length,
      points: pts,
      timeSpentMs: 0,
      userAnswer: selected,
    })
    setChecked(true)
  }
  const retry = () => {
    setSelected(Array(blanks.length).fill(null))
    setOpenPicker(null)
    setChecked(false)
    setShowSol(false)
    setRetried(true)
  }
  return (
    <ScrollView
      style={{flex: 1}}
      contentContainerStyle={{padding: 20, gap: 14, paddingBottom: 40}}
      keyboardShouldPersistTaps="handled">
      {!!slide.question && <Text style={ss.question}>{slide.question}</Text>}
      <View style={{gap: 12}}>
        {blanks.map((blank, i) => {
          const sv = selected[i]
          const isOk = checked && results[i]
          const isBad = checked && !results[i]
          const isOpen = openPicker === i && !checked
          const parts = blank.sentence.split('___')
          const before = parts[0] ?? ''
          const after = parts.slice(1).join('___')
          const beforeWords = before.trim().split(/\s+/).filter(Boolean)
          const afterWords = after.trim().split(/\s+/).filter(Boolean)
          return (
            <View
              key={blank.id}
              style={{
                borderRadius: 14,
                borderWidth: 1.5,
                padding: 14,
                gap: 10,
                borderColor: isOk
                  ? '#22c55e'
                  : isBad
                    ? '#ef4444'
                    : 'rgba(255,255,255,0.18)',
                backgroundColor: isOk
                  ? '#22c55e0a'
                  : isBad
                    ? '#ef44440a'
                    : 'rgba(255,255,255,0.04)',
              }}>
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 4,
                  rowGap: 6,
                }}>
                {beforeWords.map((w, wi) => (
                  <Text
                    key={`b${wi}`}
                    style={{
                      color: 'rgba(255,255,255,0.85)',
                      fontSize: 15,
                      lineHeight: 22,
                    }}>
                    {w}
                  </Text>
                ))}
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() => {
                    if (!checked) setOpenPicker(isOpen ? null : i)
                  }}
                  style={[
                    {
                      paddingHorizontal: 10,
                      paddingVertical: 4,
                      borderRadius: 8,
                      borderWidth: 1.5,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                    },
                    sv
                      ? {
                          borderColor: isOk
                            ? '#22c55e'
                            : isBad
                              ? '#ef4444'
                              : primaryColor + '88',
                          backgroundColor: isOk
                            ? '#22c55e18'
                            : isBad
                              ? '#ef444418'
                              : primaryColor + '22',
                        }
                      : {
                          borderStyle: 'dashed',
                          borderColor: isOpen
                            ? primaryColor
                            : 'rgba(255,255,255,0.28)',
                          backgroundColor: isOpen
                            ? primaryColor + '15'
                            : 'rgba(255,255,255,0.05)',
                        },
                  ]}>
                  <Text
                    style={{
                      fontSize: 15,
                      fontWeight: sv ? '700' : '500',
                      lineHeight: 22,
                      color: sv
                        ? isOk
                          ? '#22c55e'
                          : isBad
                            ? '#ef4444'
                            : primaryColor
                        : isOpen
                          ? primaryColor
                          : 'rgba(255,255,255,0.35)',
                    }}>
                    {sv ?? 'choose…'}
                  </Text>
                  {sv && checked ? (
                    <Feather
                      name={isOk ? 'check-circle' : 'x-circle'}
                      size={13}
                      color={isOk ? '#22c55e' : '#ef4444'}
                    />
                  ) : (
                    <Feather
                      name={isOpen ? 'chevron-up' : 'chevron-down'}
                      size={12}
                      color={
                        sv
                          ? primaryColor + '88'
                          : isOpen
                            ? primaryColor
                            : 'rgba(255,255,255,0.28)'
                      }
                    />
                  )}
                </TouchableOpacity>
                {afterWords.map((w, wi) => (
                  <Text
                    key={`a${wi}`}
                    style={{
                      color: 'rgba(255,255,255,0.85)',
                      fontSize: 15,
                      lineHeight: 22,
                    }}>
                    {w}
                  </Text>
                ))}
              </View>
              {isOpen && (
                <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 7}}>
                  {(allOptions[i] ?? []).map((opt, oi) => (
                    <TouchableOpacity
                      accessibilityRole="button"
                      key={oi}
                      onPress={() => {
                        void Haptics.impactAsync(
                          Haptics.ImpactFeedbackStyle.Light,
                        )
                        setSelected(sel => {
                          const n = [...sel]
                          n[i] = opt
                          return n
                        })
                        setOpenPicker(null)
                      }}
                      style={{
                        paddingHorizontal: 14,
                        paddingVertical: 8,
                        borderRadius: 10,
                        borderWidth: 1,
                        backgroundColor: 'rgba(255,255,255,0.07)',
                        borderColor: 'rgba(255,255,255,0.15)',
                      }}>
                      <Text style={{color: '#fff', fontSize: 14}}>{opt}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
              {showSol && (
                <Text
                  style={{color: '#22c55e', fontSize: 12, fontWeight: '600'}}>
                  ✓ {blank.answer}
                </Text>
              )}
            </View>
          )
        })}
      </View>
      {!checked && !showSol && allFilled && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          style={[ss.verifyBtn, {backgroundColor: primaryColor, marginTop: 4}]}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={ss.verifyTxt}>Check</Text>
        </TouchableOpacity>
      )}
      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={blanks.map(b => b.answer).join(' · ')}
          onNext={onNext}
          onRetry={!retried ? retry : undefined}
          onShowSolution={
            !showSol
              ? () => {
                  setShowSol(true)
                  setChecked(true)
                }
              : undefined
          }
          primaryColor={primaryColor}
        />
      )}
    </ScrollView>
  )
}

// ─── Main SlidePlayer ─────────────────────────────────────────────────────────

interface SessionData {
  correct: number
  total: number
  totalPts: number
  maxTotalPts: number
  timeTakenMs: number
}

interface SlidePlayerProps {
  capsule: CapsuleModel
  onClose: () => void
  /** Skip streak / coin recording (teacher preview mode). */
  isPreview?: boolean
  /** Extra UI injected below the action buttons on the completion screen (e.g. publish button for previews). */
  footer?: React.ReactNode
  /** Jump directly to a specific slide index on mount (used by per-slide preview). */
  initialSlideIdx?: number
}

export function SlidePlayer({
  capsule,
  onClose,
  isPreview = false,
  footer,
  initialSlideIdx = 0,
}: SlidePlayerProps) {
  const insets = useSafeAreaInsets()
  const startTime = useRef(Date.now())
  const sessionStartRef = useRef(Date.now())
  const {mutate: savePlay} = useCreatePlayMutation()
  const {data: creatorProfile} = useProfileQuery({
    did: capsule.creatorId || undefined,
  })
  const slides = (capsule.slides ?? []).filter(s => s.type !== 'video')
  const primaryColor =
    (capsule.behaviorSettings?.primaryColor as string | undefined) ?? '#0085ff'
  const gradient = getGradient(capsule.id)
  const meta = SLIDE_TYPE_META

  const [slideIdx, setSlideIdx] = useState(initialSlideIdx)
  const [results, setResults] = useState<SlideResult[]>([])
  const [completed, setCompleted] = useState(false)
  const [sessionData, setSessionData] = useState<SessionData | null>(null)

  // Volume control
  const [showVolPanel, setShowVolPanel] = useState(false)
  const [volDisplay, setVolDisplay] = useState(getMasterVolume())
  const [mutedDisplay, setMutedDisplay] = useState(getMuted())
  const volTrackWidthRef = useRef(0)
  const volPanResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: e => {
        const ratio = Math.max(
          0,
          Math.min(
            1,
            e.nativeEvent.locationX / Math.max(volTrackWidthRef.current, 1),
          ),
        )
        setVolDisplay(ratio)
        setMasterVolume(ratio)
        if (ratio > 0 && getMuted()) {
          setMutedDisplay(false)
          setMuted(false)
        }
      },
      onPanResponderMove: e => {
        const ratio = Math.max(
          0,
          Math.min(
            1,
            e.nativeEvent.locationX / Math.max(volTrackWidthRef.current, 1),
          ),
        )
        setVolDisplay(ratio)
        setMasterVolume(ratio)
        if (ratio > 0 && getMuted()) {
          setMutedDisplay(false)
          setMuted(false)
        }
      },
    }),
  ).current

  // Slide entrance animation
  const [slideAnim] = useState(() => new Animated.Value(1))
  useEffect(() => {
    slideAnim.setValue(0)
    Animated.spring(slideAnim, {
      toValue: 1,
      useNativeDriver: true,
      tension: 90,
      friction: 14,
    }).start()
  }, [slideIdx])

  /**
   * The capsule's bed, in the three states a creator can leave it in.
   *
   * A chosen track is the bed; `'none'` is the creator choosing silence; and no
   * field at all is the theme's own music, which is what every capsule written
   * before the catalogue plays. The same rule the book's player applies, because
   * it is the same feature: a creator chooses the music once and it sounds the
   * same in both kinds of work.
   */
  const startCapsuleMusic = useCallback(() => {
    const choice = musicChoiceFromRecord(capsule.backgroundMusicId)
    if (choice === undefined) {
      startBgMusic(primaryColor)
      return
    }
    startFlashbookMusic(
      capsule.themeId ?? 'default',
      resolveBackgroundMusic(MUSIC_LIBRARY, choice),
    )
  }, [capsule.backgroundMusicId, capsule.themeId, primaryColor])

  /** Both engines, because either may be the one that is playing. */
  const stopCapsuleMusic = useCallback(() => {
    stopBgMusic()
    stopFlashbookMusic()
  }, [])

  // Sound theme + background music — video slides are filtered out so music
  // always starts immediately when the capsule opens.
  useEffect(() => {
    setSoundTheme(primaryColor)
    startCapsuleMusic()
    return () => {
      stopCapsuleMusic()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capsule.id])

  const slide = slides[slideIdx]
  const slideType = slide ? meta[slide.type] : null

  const handleNext = useCallback(() => {
    const next = slideIdx + 1
    playSlideAdvance()
    if (next >= slides.length) {
      stopCapsuleMusic()
      playComplete()
      const exRes = results.filter(r => r.isCorrect !== undefined)
      const correct = exRes.filter(r => r.isCorrect).length
      const totalPts = results.reduce((sum, r) => sum + (r.points ?? 0), 0)
      const maxTotalPts = getCapsuleMaxPoints(slides)
      const timeTakenMs = Date.now() - sessionStartRef.current
      setSessionData({
        correct,
        total: Math.max(exRes.length, 1),
        totalPts,
        maxTotalPts,
        timeTakenMs,
      })
      setCompleted(true)
      if (!isPreview) {
        savePlay({
          capsuleUri: capsule.id,
          score: totalPts,
          timeMs: timeTakenMs,
          slideResults: results,
        })
      }
    } else {
      setSlideIdx(next)
      startTime.current = Date.now()
    }
  }, [
    slideIdx,
    slides.length,
    slides,
    results,
    isPreview,
    savePlay,
    capsule.id,
  ])

  const handleAnswer = useCallback(
    (r: AnswerResult) => {
      if (!slide) return
      const result: SlideResult = {
        ...r,
        slideId: slide.id,
        type: slide.type,
        timeSpentMs: Date.now() - startTime.current,
      }
      setResults(prev => [...prev.filter(x => x.slideId !== slide.id), result])
    },
    [slide],
  )

  // ── Completion screen — shown as the final "slide" after all slides are done ──
  if (completed && sessionData) {
    return (
      <View style={{flex: 1, backgroundColor: '#0a1220'}}>
        <CompletionScreen
          score={sessionData.correct}
          total={sessionData.total}
          totalPts={sessionData.totalPts}
          maxTotalPts={sessionData.maxTotalPts}
          timeTakenMs={sessionData.timeTakenMs}
          primaryColor={primaryColor}
          capsuleTitle={capsule.title}
          themeId={capsule.themeId}
          results={results}
          slides={slides}
          onReplay={() => {
            sessionStartRef.current = Date.now()
            setSlideIdx(initialSlideIdx)
            setResults([])
            setCompleted(false)
            setSessionData(null)
            startCapsuleMusic()
          }}
          onDone={onClose}
          footer={footer}
        />
      </View>
    )
  }

  if (!slide) {
    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0a1220',
        }}>
        <Text style={{color: '#fff'}}>No slides</Text>
      </View>
    )
  }

  return (
    <View style={{flex: 1}}>
      {/* ── Layer 1: background gradient ──────────────────────────────────── */}
      <LinearGradient
        colors={gradient}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 1}}
        style={StyleSheet.absoluteFill}
      />

      {/* ── Layer 2: UI chrome — header, progress, slide content ────────── */}
      {/* pointerEvents="box-none": must be a component prop, not folded into    */}
      {/* the `style` array — react-native-web only statically extracts          */}
      {/* "box-none" into a passthrough CSS class from the literal prop; merged   */}
      {/* into a style object it silently no-ops, swallowing all taps over the    */}
      {/* full-screen video (e.g. the Skip button). See app/capsule/[id].tsx.    */}
      <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
        {/* Header */}
        <View style={[ss.header, {paddingTop: insets.top + 10}]}>
          <Pressable
            accessibilityRole="button"
            onPress={onClose}
            style={ss.closeBtn}>
            <Feather name="x" size={20} color="#fff" />
          </Pressable>
          <View style={ss.headerMid}>
            <UserAvatar
              size={24}
              avatar={creatorProfile?.avatar ?? null}
              type="user"
            />
            <Text style={ss.headerName} numberOfLines={1}>
              {creatorProfile?.displayName ||
                creatorProfile?.handle ||
                capsule.title}
            </Text>
          </View>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => setShowVolPanel(v => !v)}
            style={ss.closeBtn}>
            <Feather
              name={mutedDisplay ? 'volume-x' : 'volume-2'}
              size={18}
              color="rgba(255,255,255,0.75)"
            />
          </TouchableOpacity>
        </View>

        {/* Progress bar */}
        <View style={ss.progressRow}>
          {slides.map((sl, i) => {
            const res = results.find(r => r.slideId === sl.id)
            const done = i < slideIdx || !!res
            const active = i === slideIdx
            return (
              <View
                key={sl.id}
                style={[
                  ss.seg,
                  {
                    flex: 1,
                    height: active ? 4 : 3,
                    backgroundColor: done
                      ? res?.isCorrect === false
                        ? '#ef4444'
                        : res?.isCorrect
                          ? '#22c55e'
                          : primaryColor
                      : active
                        ? primaryColor + '88'
                        : 'rgba(255,255,255,0.15)',
                  },
                ]}
              />
            )
          })}
        </View>

        {/* Counter + type */}
        <View style={ss.infoRow}>
          <Text style={ss.counter}>
            {slideIdx + 1} / {slides.length}
          </Text>
          {slideType && (
            <View
              style={[
                ss.typePill,
                {
                  backgroundColor: slideType.color + '22',
                  borderColor: slideType.color + '55',
                },
              ]}>
              <Feather
                name={slideType.icon as FeatherName}
                size={11}
                color={slideType.color}
              />
              <Text style={[ss.typePillTxt, {color: slideType.color}]}>
                {slideType.label}
              </Text>
            </View>
          )}
        </View>

        {/* Slide content — animated entrance */}
        <Animated.View
          style={{
            flex: 1,
            opacity: slideAnim,
            transform: [
              {
                translateY: slideAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [24, 0],
                }),
              },
            ],
          }}>
          <ScrollView
            style={{flex: 1}}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: insets.bottom + 80,
            }}
            keyboardShouldPersistTaps="handled">
            {slide.type === 'content' && (
              <ContentSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onNext={handleNext}
              />
            )}
            {slide.type === 'single_choice' && (
              <SingleChoiceSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'multiple_choice' && (
              <MultipleChoiceSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'true_false' && (
              <TrueFalseSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'matching' && (
              <MatchingSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'ordering' && (
              <OrderingSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'image_choice' && (
              <ImageChoiceSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'accordion' && (
              <AccordionSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onNext={handleNext}
              />
            )}
            {slide.type === 'drag_words' && (
              <DragWordsSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'fill_blank' && (
              <FillBlankSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'fill_blanks' && (
              <FillBlanksSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'find_words' && (
              <FindWordsSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'mark_words' && (
              <MarkWordsSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'complex_fill_blank' && (
              <ComplexFillBlankSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onAnswer={handleAnswer}
                onNext={handleNext}
              />
            )}
            {slide.type === 'text_block' && (
              <TextBlockSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onNext={handleNext}
              />
            )}
            {slide.type === 'image_slide' && (
              <ImageBlockSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onNext={handleNext}
              />
            )}
            {slide.type === 'table_slide' && (
              <TableBlockSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onNext={handleNext}
              />
            )}
            {slide.type === 'chart_slide' && (
              <ChartBlockSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onNext={handleNext}
              />
            )}
            {slide.type === 'formula_slide' && (
              <FormulaBlockSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onNext={handleNext}
              />
            )}
            {slide.type === 'drawing_slide' && (
              <DrawingBlockSlide
                key={slide.id}
                slide={slide}
                primaryColor={primaryColor}
                onNext={handleNext}
              />
            )}
            {/* Fallback for any unrecognised slide type — prevents a blank/stuck screen */}
            {![
              'content',
              'single_choice',
              'multiple_choice',
              'true_false',
              'matching',
              'ordering',
              'image_choice',
              'accordion',
              'drag_words',
              'fill_blank',
              'fill_blanks',
              'find_words',
              'mark_words',
              'complex_fill_blank',
              'text_block',
              'image_slide',
              'table_slide',
              'chart_slide',
              'formula_slide',
              'drawing_slide',
            ].includes(slide.type) && (
              <View style={ss.content}>
                <Feather
                  name="alert-triangle"
                  size={28}
                  color="rgba(255,255,255,0.3)"
                  style={{alignSelf: 'center', marginBottom: 8}}
                />
                <Text
                  style={[ss.contentBody, {textAlign: 'center', opacity: 0.5}]}>
                  Unknown slide type: {slide.type}
                </Text>
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={handleNext}
                  style={[ss.primaryBtn, {backgroundColor: primaryColor}]}>
                  <Text style={ss.primaryBtnTxt}>Skip</Text>
                  <Feather name="arrow-right" size={16} color="#fff" />
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        </Animated.View>
      </View>

      {/* ── Layer 4: Volume panel — topmost so backdrop intercepts all touches */}
      {showVolPanel && (
        <Pressable
          accessibilityRole="button"
          style={StyleSheet.absoluteFill}
          onPress={() => setShowVolPanel(false)}>
          <Pressable
            accessibilityRole="button"
            onPress={e => e.stopPropagation()}
            style={[ss.volPanel, {top: insets.top + 54}]}>
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() => {
                const m = !mutedDisplay
                setMutedDisplay(m)
                setMuted(m)
              }}
              style={ss.volMuteBtn}>
              <Feather
                name={
                  mutedDisplay
                    ? 'volume-x'
                    : volDisplay > 0.5
                      ? 'volume-2'
                      : 'volume-1'
                }
                size={18}
                color="#fff"
              />
            </TouchableOpacity>
            <View
              style={ss.volSliderTrack}
              onLayout={e => {
                volTrackWidthRef.current = e.nativeEvent.layout.width
              }}
              {...volPanResponder.panHandlers}>
              <View
                style={[
                  ss.volSliderFill,
                  {
                    width: `${Math.round(volDisplay * 100)}%`,
                    backgroundColor: primaryColor,
                  },
                ]}
              />
              <View
                style={[
                  ss.volSliderThumb,
                  {
                    left: `${Math.round(volDisplay * 100)}%`,
                    borderColor: primaryColor,
                  },
                ]}
              />
            </View>
            <Text style={ss.volPct}>{Math.round(volDisplay * 100)}%</Text>
          </Pressable>
        </Pressable>
      )}
    </View>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const ss = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 10,
    gap: 12,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerMid: {flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8},
  headerName: {color: '#fff', fontSize: 13, fontWeight: '600'},
  progressRow: {
    flexDirection: 'row',
    gap: 3,
    paddingHorizontal: 16,
    marginBottom: 10,
  },
  seg: {borderRadius: 2},
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 6,
  },
  counter: {color: 'rgba(255,255,255,0.4)', fontSize: 12},
  typePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderWidth: 1,
  },
  typePillTxt: {fontSize: 11, fontWeight: '700'},
  content: {flex: 1, padding: 20, gap: 14},
  videoSlide: {flex: 1, position: 'relative', backgroundColor: '#000'},
  videoWrap: {...StyleSheet.absoluteFillObject},
  video: {width: '100%', height: '100%'},
  videoCenter: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoPlayBtn: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  endedBadge: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#22c55e55',
  },
  endedTxt: {color: '#22c55e', fontSize: 12, fontWeight: '700'},
  videoInfo: {flex: 1, padding: 20, gap: 10, justifyContent: 'center'},
  videoBox: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.05)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    gap: 10,
  },
  videoBoxLabel: {color: 'rgba(255,255,255,0.25)', fontSize: 13},
  videoTitle: {color: '#fff', fontSize: 22, fontWeight: '800', lineHeight: 28},
  videoDesc: {color: 'rgba(255,255,255,0.65)', fontSize: 15, lineHeight: 22},
  contentH2: {color: '#fff', fontSize: 22, fontWeight: '800', lineHeight: 28},
  contentBody: {color: 'rgba(255,255,255,0.75)', fontSize: 15, lineHeight: 24},
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 14,
    marginTop: 8,
  },
  primaryBtnTxt: {color: '#fff', fontSize: 15, fontWeight: '700'},
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 20,
    borderWidth: 1,
  },
  badgeTxt: {fontSize: 11, fontWeight: '700'},
  badgeHint: {fontSize: 10, opacity: 0.7},
  question: {color: '#fff', fontSize: 19, fontWeight: '700', lineHeight: 26},
  optList: {gap: 10},
  optRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 13,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: 1.5,
  },
  optRadio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optDot: {width: 10, height: 10, borderRadius: 5},
  optLabel: {flex: 1, color: '#fff', fontSize: 14, fontWeight: '500'},
  tfRow: {flexDirection: 'row', gap: 12},
  tfBtn: {
    flex: 1,
    borderRadius: 16,
    borderWidth: 2,
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  tfLabel: {fontSize: 20, fontWeight: '800'},
  tfCheck: {position: 'absolute', top: 8, right: 8},
  blanksWrap: {gap: 12},
  blankGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  blankCtx: {color: 'rgba(255,255,255,0.8)', fontSize: 15, lineHeight: 22},
  blankInputWrap: {flexDirection: 'row', alignItems: 'center', gap: 6},
  blankInput: {
    minWidth: 100,
    height: 40,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    fontSize: 14,
  },
  matchGrid: {flexDirection: 'row', gap: 8, alignItems: 'flex-start'},
  matchCol: {flex: 1, gap: 8},
  matchArrow: {justifyContent: 'center', paddingTop: 18},
  matchItem: {
    borderRadius: 10,
    borderWidth: 1.5,
    padding: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 5,
    minHeight: 44,
  },
  matchTxt: {color: '#fff', fontSize: 13, fontWeight: '500', flex: 1},
  matchHint: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  orderHint: {color: 'rgba(255,255,255,0.5)', fontSize: 13},
  orderList: {gap: 8},
  orderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 12,
    borderWidth: 1.5,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  orderNum: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  orderNumTxt: {fontSize: 12, fontWeight: '800'},
  orderLabel: {flex: 1, color: '#fff', fontSize: 14, fontWeight: '500'},
  orderArrows: {flexDirection: 'row', gap: 2},
  imgGrid: {flexDirection: 'row', flexWrap: 'wrap', gap: 10},
  imgItem: {
    width: '47%',
    borderRadius: 14,
    borderWidth: 1.5,
    overflow: 'hidden',
  },
  imgPlaceholder: {
    width: '100%',
    aspectRatio: 4 / 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imgLabel: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
    padding: 8,
    textAlign: 'center',
  },
  imgBadge: {position: 'absolute', top: 8, right: 8},
  verifyBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 14,
    marginTop: 4,
  },
  verifyTxt: {color: '#fff', fontSize: 15, fontWeight: '700'},
  fbBar: {
    marginTop: 12,
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
    gap: 10,
  },
  fbTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fbMsg: {flexDirection: 'row', alignItems: 'center', gap: 7, flex: 1},
  fbMsgTxt: {fontSize: 14, fontWeight: '700', flex: 1},
  fbScore: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    borderWidth: 1,
  },
  fbScoreTxt: {fontSize: 12, fontWeight: '800'},
  fbExp: {color: 'rgba(255,255,255,0.65)', fontSize: 13, lineHeight: 18},
  fbSol: {color: '#f59e0b', fontSize: 13, lineHeight: 18, fontStyle: 'italic'},
  fbActions: {flexDirection: 'row', gap: 8, flexWrap: 'wrap'},
  ghostBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  ghostTxt: {fontSize: 13, fontWeight: '700'},
  nextBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 20,
    marginLeft: 'auto',
  },
  nextTxt: {color: '#fff', fontSize: 14, fontWeight: '700'},
  // Volume panel
  volPanel: {
    position: 'absolute',
    right: 10,
    zIndex: 100,
    elevation: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: 'rgba(10,18,32,0.92)',
    borderRadius: 30,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    width: 240,
  },
  volMuteBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  volSliderTrack: {
    flex: 1,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.1)',
    position: 'relative',
    justifyContent: 'center',
  },
  volSliderFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 13,
    opacity: 0.8,
  },
  volSliderThumb: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderRadius: 10,
    top: 3,
    marginLeft: -10,
    borderWidth: 2,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOpacity: 0.35,
    shadowRadius: 4,
    shadowOffset: {width: 0, height: 2},
    elevation: 4,
  },
  volPct: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 11,
    fontWeight: '700',
    minWidth: 28,
    textAlign: 'right',
  },
  // Finish capsule button (last non-video slide — Layer 4 sibling)
  // Video fullscreen overlays
  videoInfoOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 24,
    paddingBottom: 72,
    gap: 8,
  },
  videoSkipBtn: {
    position: 'absolute',
    bottom: 24,
    right: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(0,0,0,0.45)',
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  videoSkipTxt: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    fontWeight: '700',
  },
})
